import { resolveCompleteMetaRecovery } from "../platformAnalytics/metaRecoveryEvidence";
import { hasReportedSnapshotZero, snapshotScopeMatches, canonicalSnapshotChannel, canonicalReportingDay, resolveEffectiveReportingTimeframe, resolveReportingCampaignSelection, canonicalReportingScopeMatches } from "../platformAnalytics/reportedSnapshotEvidence";
import { readReportedCompletionCount } from '../platformAnalytics/reportedCompletionCount';
import { REPORTING_TIME_ZONE, reportingToday, responseReportingTimeZone } from "../utils/reportingCalendar";
import { selectApprovedPlanCampaign } from "../utils/optimusPacing";
import { readYoutubeQuartileRates } from "../platformAnalytics/youtubeQuartileRates";
import {
  formatFlightDate,
  formatFlightDateShort,
  formatFlightRange,
} from "../utils/flightDates";
import {
  deriveCrossChannelCtrPercent,
  deriveCtrPercentFromClicks,
} from "../utils/canonicalCtr";
import { aggregateMetricBasis } from "../platformAnalytics/metricBasis";
import { resolveClientSpendBasisLabel } from "../platformAnalytics/spendEfficiencyContract";
import { getCanonicalCampaignDisplayName } from "../utils/campaignDisplayName";
import { resolveGovernedReportingWindowScope } from "../platformAnalytics/reportingWindowLabel";
import {
  resolveSpendEfficiencyContract,
  resolveSpendEfficiencyMetric,
  resolveSpendEfficiencySpend,
} from "../platformAnalytics/spendEfficiencyContract";

const normalizeBase = (base) => {
  const text = String(base || "").trim();
  if (!text) return "";
  return text.replace(/\/+$/, "");
};

const DEFAULT_PRODUCTION_BACKEND_URL = "https://cphere-backend.onrender.com";
const API_BASE_URL = normalizeBase(
  process.env.REACT_APP_BACKEND_URL ||
    (process.env.NODE_ENV === "production" ? DEFAULT_PRODUCTION_BACKEND_URL : "")
);
const DATA_CACHE_TTL_MS = 20 * 1000;
const CONNECTED_PLATFORM_TOPLINE_VERSION = "connected_platform_topline_v7_eligible_rates";
const CONNECTED_PLATFORM_REQUEST_DEBUG_KEYS = new Set([
  "facebook",
  "instagram",
  "youtube",
]);

const dataCache = new Map();

const PLATFORM_LABELS = {
  facebook: "Facebook",
  instagram: "Instagram",
  tiktok: "TikTok",
  youtube: "YouTube",
  x: "X",
  truthsocial: "Truth Social",
  truth_social: "Truth Social",
  rumble: "Rumble",
  googlesearch: "Google Search",
  google_search: "Google Search",
  google: "Google Search",
  search: "Google Search",
  reddit: "Reddit",
  amazon: "Amazon",
  shopify: "Shopify",
  shopify_store: "Shopify",
  linkedin: "LinkedIn Ads",
  linkedin_ads: "LinkedIn Ads",
  metricool: "Metricool",
  community: "Metricool",
  audio: "Audio",
  ingame: "In-Game",
  in_game: "In-Game",
  stackadapt: "Cphere Programmatic",
  ctv: "CTV",
  video: "Video",
  mobile: "Mobile",
  display: "Display",
  dooh: "DOOH",
  email: "Email",
};

const PROGRAMMATIC_PRICING_CHANNEL_KEYS = new Set([
  "audio",
  "ctv",
  "display",
  "dooh",
  "ingame",
  "mobile",
  "video",
]);
function getAuthToken() {
  try {
    return (
      localStorage.getItem("authToken") ||
      localStorage.getItem("clientAuthToken") ||
      localStorage.getItem("token") ||
      localStorage.getItem("accessToken") ||
      ""
    );
  } catch {
    return "";
  }
}

function apiUrl(path) {
  const safePath = String(path || "").startsWith("/") ? path : `/${path}`;
  if (API_BASE_URL) return `${API_BASE_URL}${safePath}`;
  if (typeof window !== "undefined" && window.location?.origin) {
    return `${window.location.origin}${safePath}`;
  }
  return safePath;
}

function getWindowOrigin() {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  return null;
}

function safeDebugPayload(value) {
  if (value === undefined) return null;
  if (value instanceof Date) return value.toISOString();
  if (Array.isArray(value)) return value.map((entry) => safeDebugPayload(entry));
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, entry]) => [key, safeDebugPayload(entry)])
    );
  }
  return value;
}

function logPlatformRequestDebug(stage, payload = {}) {
  const event = {
    kind: "platform_request",
    stage,
    ts: new Date().toISOString(),
    ...safeDebugPayload(payload),
  };
  try {
    if (typeof window !== "undefined") {
      window.__CPHERE_ANALYTICS_DEBUG = Array.isArray(window.__CPHERE_ANALYTICS_DEBUG)
        ? window.__CPHERE_ANALYTICS_DEBUG
        : [];
      window.__CPHERE_ANALYTICS_DEBUG.push(event);
    }
  } catch {}
  try {
    console.info(
      "[analyticsToplineService][platform-request-debug]",
      JSON.stringify(event)
    );
  } catch {
    console.info("[analyticsToplineService][platform-request-debug]", stage, payload);
  }
}

async function fetchJson(path, { signal, method = "GET", body } = {}) {
  const token = getAuthToken();
  const resolvedUrl = apiUrl(path);
  const response = await fetch(resolvedUrl, {
    method,
    signal,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(body ? { body: JSON.stringify(body) } : {}),
  });

  const text = await response.text().catch(() => "");
  let json = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = null;
  }

  if (
    String(path || "").includes("/api/optimus/snapshot?") &&
    CONNECTED_PLATFORM_REQUEST_DEBUG_KEYS.has(
      String(new URLSearchParams(String(path).split("?")[1] || "").get("channel") || "")
        .trim()
        .toLowerCase()
    )
  ) {
    logPlatformRequestDebug("raw_fetch_response", {
      app: "client",
      requestUrl: resolvedUrl,
      ok: response.ok,
      status: response.status,
      rawText: text,
      parsedBody: json,
    });
  }

  if (!response.ok) {
    const error = new Error(
      json?.error || json?.message || text || `Request failed (${response.status})`
    );
    error.status = response.status;
    error.payload = json;
    throw error;
  }

  return json;
}

function toNumber(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === "string" && !value.trim()) return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

function hasPositiveSignal(...values) {
  return values.some((value) => {
    const num = toNumber(value);
    return num != null && num > 0;
  });
}

function toDate(value) {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const text = String(value).trim();
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    const [, year, month, day] = match;
    return new Date(Number(year), Number(month) - 1, Number(day), 12);
  }
  const parsed = new Date(text);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function normalizePercent(value) {
  const num = toNumber(value);
  if (num == null) return null;
  if (num > 0 && num <= 1) return num * 100;
  return num;
}

function coerceSnapshotFound(snapshot) {
  if (snapshot?.found === true) return true;
  if (snapshot?.found === false) return false;
  return null;
}

function pickFirstNumber(...values) {
  for (const value of values) {
    const num = toNumber(value);
    if (num != null) return num;
  }
  return null;
}

function pickFirstMetricValue(...values) {
  for (const value of values) {
    if (value !== null && value !== undefined && String(value).trim() !== "") return value;
  }
  return null;
}

/*
 * Field aliases for one governed metric bag, in preference order.
 *
 * The snapshot endpoint finalizes its window totals and each day of its daily
 * series through the same functions, so both arrive in the same vocabulary.
 * Reading them through one table is what keeps a day and the window above it
 * from ever being read on different fields — which is the only way a bar can
 * silently stop adding up to the number it sits under.
 *
 * Wave 3: spend is served billed-or-null. `spend` is itself billed-or-null for
 * client tokens; the old provider/net tails (`mediaCost`, `cost`) are gone —
 * no raw fallback, fail closed.
 */
const CANONICAL_METRIC_ALIASES = Object.freeze({
  spend: ["billedSpend", "clientFacingSpend", "spend"],
  impressions: ["impressions", "impression", "totalImpressions"],
  clicks: ["clicks", "click", "totalClicks"],
  conversions: ["conversions", "conversion"],
  engagements: ["engagements", "engagement"],
  videoViews: ["trueViewViews", "true_view_views", "videoViews", "video_views", "views"],
  videoStarts: [
    "videoStarted",
    "video_started",
    "videoStarts",
    "video_start",
    "video_starts",
    "pageStarts",
    "page_starts",
  ],
  videoCompletions: [
    "videoCompletions100",
    "youtubeVideoCompletions",
    "videoCompletions",
    "video_completions",
    "videoCompleted",
    "video_completed",
    "videoCompletes",
    "video_complete",
    "completedViews",
    "completed_views",
    "completeViews",
    "views100",
    "videoViews100",
    "quartile_100",
    "q100",
  ],
});

const CANONICAL_RATE_ALIASES = Object.freeze({
  ctr: ["ctr", "clickThroughRate", "click_through_rate"],
  vcr: ["videoCompletionRate", "completionRate", "vcr"],
});

function readCanonicalMetricAlias(metrics, key) {
  return pickFirstNumber(...CANONICAL_METRIC_ALIASES[key].map((field) => metrics?.[field]));
}

/*
 * One governed metric bag, read into the vocabulary the platform pages use.
 * Rates are taken as the source reported them and only derived when the
 * source did not report one — this layer never re-derives a rate that already
 * exists, so a page cannot disagree with the reporting service about it.
 */
export function readCanonicalMetricBundle(metrics) {
  const spend = readCanonicalMetricAlias(metrics, "spend");
  const impressions = readCanonicalMetricAlias(metrics, "impressions");
  const videoStarts = readCanonicalMetricAlias(metrics, "videoStarts");
  const videoCompletions = readCanonicalMetricAlias(metrics, "videoCompletions");
  return {
    spend,
    impressions,
    clicks: readCanonicalMetricAlias(metrics, "clicks"),
    conversions: readCanonicalMetricAlias(metrics, "conversions"),
    engagements: readCanonicalMetricAlias(metrics, "engagements"),
    videoViews: readCanonicalMetricAlias(metrics, "videoViews"),
    videoStarts,
    videoCompletions,
    videoPlayedTo100Count: readReportedCompletionCount(metrics?.videoPlayedTo100Count),
    ...readYoutubeQuartileRates(metrics),
    ctr: normalizePercent(
      pickFirstMetricValue(...CANONICAL_RATE_ALIASES.ctr.map((field) => metrics?.[field]))
    ),
    cpm:
      spend != null && impressions != null && impressions > 0
        ? (spend / impressions) * 1000
        : pickFirstNumber(metrics?.cpm, metrics?.costPerMille),
    /*
     * Provided-first, honoring this bundle's own doctrine: a completion
     * rate the reporting service already stated (eligibility-scoped,
     * STARTS-pinned) is never re-derived from totals — the local pairing
     * survives only when the source reported no rate at all.
     */
    vcr: (() => {
      const provided = normalizePercent(
        pickFirstMetricValue(...CANONICAL_RATE_ALIASES.vcr.map((field) => metrics?.[field]))
      );
      if (provided != null) return provided;
      /*
       * Wrapped in the same normalizer the provided path uses. Without it this
       * branch returned a FRACTION where its sibling returned a percent, so a
       * source that reported completions and starts but no rate of its own
       * rendered a 98.2% completion rate as "0.98%" — one function quietly
       * speaking two scales.
       */
      return videoStarts != null && videoStarts > 0 && videoCompletions != null
        ? normalizePercent(videoCompletions / videoStarts)
        : null;
    })(),
  };
}

/*
 * Daily platform rows keep the shared generic video fields because CTV,
 * TikTok, and the other video profiles still consume them. YouTube also has
 * stricter presentation names, though, so its dated rows expose those names
 * as aliases of the same canonical values. This is deliberately scoped to a
 * daily YouTube projection: it does not rename the shared fields or create a
 * second calculation path.
 */
export function readCanonicalDailyMetricBundle(metrics, platformKey) {
  const bundle = readCanonicalMetricBundle(metrics);
  const normalizedPlatformKey = String(platformKey || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  if (normalizedPlatformKey !== "youtube" && normalizedPlatformKey !== "google_youtube") {
    return bundle;
  }

  /*
   * YouTube daily CTR has one unambiguous unit: a ratio in [0, 1], derived
   * from the same day's canonical counts. Google may report `0.807` to mean
   * `0.807%`; the generic rate normalizer cannot distinguish that percent-unit
   * value from the ratio `0.807`. Recomputing from counts prevents that value
   * from being scaled to `80.7%` and keeps row percentages out of the daily
   * contract entirely. The presentation layer owns ratio -> percent display.
   */
  const dailyCtrRatio =
    bundle.clicks != null && bundle.impressions != null && bundle.impressions > 0
      ? bundle.clicks / bundle.impressions
      : null;

  return {
    ...bundle,
    ctr: dailyCtrRatio,
    trueViewViews: pickFirstNumber(
      metrics?.trueViewViews,
      metrics?.true_view_views,
      bundle.videoViews
    ),
    youtubeVideoCompletions: pickFirstNumber(
      metrics?.youtubeVideoCompletions,
      metrics?.videoCompletions100,
      metrics?.video_completions_100,
      bundle.videoCompletions
    ),
  };
}

/*
 * The governed per-day series for the requested platform, exactly as the
 * snapshot endpoint projected it.
 *
 * Nothing is derived and nothing is filled in: a day the source never
 * reported simply is not in the list, because an unreported day and an
 * observed zero are different facts and only the source may say which one
 * applies.
 *
 * The series is dropped when several channels were fetched and merged into
 * one window, since the endpoint projects each channel's days separately and
 * one channel's series would not reconcile with a merged window.
 */
function resolveSnapshotDailyDelivery(snapshot, { merged = false } = {}) {
  if (merged) return null;
  const daily = snapshot?.dailyDelivery;
  if (!daily || !Array.isArray(daily.days)) return null;

  const platformKey = daily.channel || snapshot?.channel || null;

  const days = daily.days
    .filter((day) => day && typeof day.date === "string" && day.metrics)
    .map((day) => ({
      date: day.date,
      metrics: readCanonicalDailyMetricBundle(sanitizeSnapshotMetrics(day.metrics), platformKey),
    }));
  if (!days.length) return null;

  return {
    channel: daily.channel || null,
    days,
    observedDays: days.length,
    complete: daily.complete === true,
    undecomposableChannels: Array.isArray(daily.undecomposableChannels)
      ? daily.undecomposableChannels
      : [],
  };
}

/*
 * Dated evidence from the same canonical performance-summary object that owns
 * the KPI totals. A missing source day stays absent; the flight's unreported
 * future dates are never synthesized as zero rows.
 */
function resolveCanonicalDailyDeliveryForPlatform(canonicalSummary, platformKey) {
  const daily = canonicalSummary?.dailyByChannel;
  if (!daily || !Array.isArray(daily.days) || !platformKey) return null;

  const days = daily.days
    .map((day) => {
      if (!day || typeof day.date !== "string") return null;
      const entry = day.byChannel?.[platformKey];
      const metrics = entry?.metrics || entry;
      if (!metrics || typeof metrics !== "object") return null;
      return {
        date: day.date,
        metrics: readCanonicalDailyMetricBundle(metrics, platformKey),
      };
    })
    .filter(Boolean);
  if (!days.length) return null;

  const undecomposableChannels = (
    Array.isArray(daily.undecomposableChannels) ? daily.undecomposableChannels : []
  ).filter((key) => key === platformKey);

  return {
    channel: platformKey,
    days,
    observedDays: days.length,
    complete: daily.complete === true && undecomposableChannels.length === 0,
    undecomposableChannels,
  };
}

/*
 * The observed GA4 site-visit series for this window, exactly as the governed
 * reporting payload published it.
 *
 * MEASUREMENT ONLY. Site visits describe the ADVERTISER'S SITE during these
 * dates — not traffic this platform delivered, and not traffic attributable to
 * it. Nothing downstream may sum this into spend, impressions, clicks, video
 * views or conversions, and nothing may use it as the denominator of a media
 * rate. It reaches the page as its own field for exactly that reason: a value
 * that lives beside the delivery metrics cannot be mistaken for one of them.
 *
 * A day the property did not report is ABSENT, never a zero — a zero here is an
 * observation the source actually made, and the two must stay distinguishable
 * all the way to the chart. Re-filtered on arrival rather than trusted: this is
 * the last place before a value becomes a plotted point.
 */
function resolveObservedSiteVisits(siteVisits) {
  if (!siteVisits || typeof siteVisits !== "object") return null;
  const isIsoDay = (value) => /^\d{4}-\d{2}-\d{2}$/.test(String(value || ""));
  const days = (Array.isArray(siteVisits.days) ? siteVisits.days : [])
    .filter(
      (day) =>
        day &&
        isIsoDay(day.date) &&
        typeof day.value === "number" &&
        Number.isFinite(day.value)
    )
    .map((day) => ({ date: day.date, value: day.value }))
    .sort((a, b) => a.date.localeCompare(b.date));
  if (!days.length) return null;
  return {
    metric: "sessions",
    days,
    observedDays: days.length,
    startDate: isIsoDay(siteVisits.startDate) ? siteVisits.startDate : null,
    endDate: isIsoDay(siteVisits.endDate) ? siteVisits.endDate : null,
    timeZone:
      typeof siteVisits.timeZone === "string" && siteVisits.timeZone.trim()
        ? siteVisits.timeZone.trim()
        : null,
  };
}

function hasUsableSnapshotMetrics(snapshot) {
  const metrics = getSnapshotMetricBag(snapshot);
  return hasPositiveSignal(
    metrics?.spend,
    metrics?.billedSpend,
    metrics?.clientFacingSpend,
    metrics?.impressions,
    metrics?.clicks,
    metrics?.conversions,
    metrics?.engagements,
    metrics?.videoViews,
    metrics?.video_views,
    metrics?.videoStarts,
    metrics?.video_started,
    metrics?.videoCompletions,
    metrics?.video_completed,
    metrics?.sessions
  );
}

function isAutoScopedTimeframe(timeframe) {
  if (!timeframe || timeframe.isExplicitOverride === true) return false;
  const source = String(timeframe.source || "").toLowerCase();
  return !source || source.includes("default") || source.includes("flight");
}

function isLatestSnapshotFallback(snapshot) {
  if (!snapshot || typeof snapshot !== "object") return false;
  // Stored contributors can set usedFallback while exactly matching the
  // requested period. Only explicit evidence of a different window is a
  // latest-window fallback; source provenance does not erase measurements.
  const sourceText = [
    snapshot.source,
    snapshot.sourceTier,
    snapshot.sourceLabel,
    ...(Array.isArray(snapshot.sourceNotes) ? snapshot.sourceNotes : []),
    ...(Array.isArray(snapshot.sourceLimitations) ? snapshot.sourceLimitations : []),
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  return sourceText.includes("latest stored") || sourceText.includes("not the exact requested");
}

// Unavailable reporting may recover in a separately validated period. Explicit
// denials, contradictory identity, and malformed envelopes are refusals; they
// cannot be converted into missing data or leave positive measurements behind.
function hasContradictorySnapshotEnvelope(snapshot) {
  if (snapshot == null) return false;
  if (typeof snapshot !== "object" || Array.isArray(snapshot)) return true;
  if (snapshot.ok === false || snapshot.error || snapshot.errorCode) return true;
  if (Object.prototype.hasOwnProperty.call(snapshot, "found") && typeof snapshot.found !== "boolean") return true;
  return snapshot.found === false && (
    hasUsableSnapshotMetrics(snapshot) || getSnapshotMetricBag(snapshot).hasReportedDeliveryData === true
  );
}

// The canonical endpoint can explicitly identify an older stored period when
// the requested period has no reporting. Its measurements remain unavailable
// here. Only a fully consistent, same-identity historical envelope permits a
// new request; a fallback flag or changed response dates alone supplies no proof.
function isIdentifiedHistoricalFallback(snapshot, scope) {
  if (snapshot?.usedFallback !== true || snapshot.found !== true || hasContradictorySnapshotEnvelope(snapshot)) return false;
  const window = snapshot.window;
  const validDay = value => typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value) &&
    Number.isFinite(Date.parse(`${value}T12:00:00Z`)) && new Date(`${value}T12:00:00Z`).toISOString().slice(0, 10) === value;
  if (!validDay(window?.start) || !validDay(window?.end) || window.start > window.end ||
      !validDay(scope.timeframe?.start) || window.end >= scope.timeframe.start) return false;
  const stored = snapshot.snapshot;
  if (stored?.sourceContract?.contract !== "canonical_reporting_v1" ||
      stored.date_start !== window.start || stored.date_end !== window.end ||
      !Array.isArray(stored.channels) || stored.channels.length !== 1 ||
      canonicalSnapshotChannel(stored.channels[0]) !== canonicalSnapshotChannel(scope.channel) ||
      snapshot.advertiserId == null || snapshot.channel == null ||
      (scope.campaignId != null && snapshot.campaignId == null)) return false;
  const summaries = [snapshot.canonicalSummary, snapshot.canonicalReporting].filter(value => value != null);
  if (!summaries.length || summaries.some(summary => !summary || typeof summary !== "object" || Array.isArray(summary) ||
      summary.contract !== "canonical_reporting_v1" || summary.ok === false || summary.error || summary.errorCode ||
      summary.advertiserId == null || (scope.campaignId != null && summary.campaignId == null) ||
      summary.scope !== (scope.campaignId != null ? "campaign" : "active_campaigns") ||
      summary.timeframe?.start !== window.start || summary.timeframe?.end !== window.end)) return false;
  return snapshotScopeMatches(snapshot, { ...scope, timeframe: window });
}

function shouldAttemptRollingLiveRecovery({ snapshot, normalizedPlatformKey, normalizedTimeframe, reportedZero = false }) {
  if (reportedZero) return false;
  if (!normalizedPlatformKey || normalizedPlatformKey === "all") return false;
  if (!isAutoScopedTimeframe(normalizedTimeframe)) return false;
  return coerceSnapshotFound(snapshot) !== true || !hasUsableSnapshotMetrics(snapshot);
}

function shouldUseRecoverySnapshot(snapshot, reportedZero = false) {
  if (reportedZero) return true;
  if (coerceSnapshotFound(snapshot) !== true || !hasUsableSnapshotMetrics(snapshot)) return false;
  if (snapshot?.isLive === true) return true;
  const sourceText = [
    snapshot?.source,
    snapshot?.sourceTier,
    snapshot?.sourceLabel,
    snapshot?.snapshot?.data_source,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  if (sourceText.includes("live")) return true;
  return !isLatestSnapshotFallback(snapshot);
}

function sanitizeSnapshotMetrics(metrics) {
  const result = { ...metrics };
  // Numeric readers must not coerce booleans/arrays or invoke an object's
  // primitive conversion. Structured authority and rate-basis evidence stays
  // intact under its own keys and validators.
  const numericKeys = new Set([
    ...Object.values(CANONICAL_METRIC_ALIASES).flat(),
    ...Object.values(CANONICAL_RATE_ALIASES).flat(),
    "cpm", "costPerMille", "cpc", "costPerClick", "cpa", "costPerAcquisition",
    "sessions", "billedCpc", "clientFacingCpc", "billedCpm", "clientFacingCpm",
  ]);
  for (const [key, value] of Object.entries(result)) {
    if (key === "hasReportedDeliveryData") continue;
    if (typeof value === "boolean" || Array.isArray(value) ||
        (numericKeys.has(key) && value != null && typeof value !== "number" && typeof value !== "string")) result[key] = null;
  }
  return result;
}

function getSnapshotMetricBag(snapshot) {
  if (!snapshot || typeof snapshot !== "object") return {};
  const nestedSnapshot = snapshot.snapshot && typeof snapshot.snapshot === "object" ? snapshot.snapshot : {};
  const aggregate = snapshot.aggregate && typeof snapshot.aggregate === "object" ? snapshot.aggregate : {};
  const totals = snapshot.totals && typeof snapshot.totals === "object" ? snapshot.totals : {};
  const metrics = snapshot.metrics && typeof snapshot.metrics === "object" ? snapshot.metrics : {};
  const nestedMetrics =
    nestedSnapshot.metrics && typeof nestedSnapshot.metrics === "object" ? nestedSnapshot.metrics : {};
  const nestedKpis =
    nestedSnapshot.kpis && typeof nestedSnapshot.kpis === "object" ? nestedSnapshot.kpis : {};

  return sanitizeSnapshotMetrics({
    ...aggregate,
    ...totals,
    ...metrics,
    ...nestedMetrics,
    ...nestedKpis,
    ...(snapshot.kpis || {}),
  });
}

function isIngestedSnapshotSource(snapshotOrRaw = {}) {
  const sourceText = [
    snapshotOrRaw.source,
    snapshotOrRaw.sourceTier,
    snapshotOrRaw.sourceLabel,
    snapshotOrRaw.dataSource,
    snapshotOrRaw.snapshotSource,
    snapshotOrRaw.snapshotSourceTier,
    snapshotOrRaw.snapshotSourceLabel,
    snapshotOrRaw.snapshotDataSource,
    snapshotOrRaw?.snapshot?.data_source,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return /ingest|manual|upload|csv|stored_snapshot|snapshot_fallback|fallback/.test(sourceText);
}

function getSnapshotSourceLabel(snapshot) {
  if (!snapshot) return null;
  if (isIngestedSnapshotSource(snapshot)) return "Ingested";
  return snapshot?.sourceLabel || null;
}

function extractSnapshotWindow(snapshot) {
  const windowPayload = snapshot?.window || snapshot?.timeframe || snapshot?.snapshot || {};
  const start = String(
    windowPayload.startDate ||
      windowPayload.start ||
      windowPayload.date_start ||
      windowPayload.dateStart ||
      snapshot?.startDate ||
      snapshot?.date_start ||
      ""
  )
    .trim()
    .slice(0, 10);
  const end = String(
    windowPayload.endDate ||
      windowPayload.end ||
      windowPayload.date_end ||
      windowPayload.dateEnd ||
      snapshot?.endDate ||
      snapshot?.date_end ||
      ""
  )
    .trim()
    .slice(0, 10);

  return start && end ? { start, end } : null;
}

function formatCurrency(value, digits = 0) {
  const num = toNumber(value);
  if (num == null) return null;
  return `$${num.toLocaleString("en-US", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}`;
}

function formatInteger(value) {
  const num = toNumber(value);
  if (num == null) return null;
  return Math.round(num).toLocaleString("en-US");
}

function formatPercent(value, digits = 2) {
  const num = normalizePercent(value);
  if (num == null) return null;
  return `${num.toFixed(digits)}%`;
}

function formatPercentScale(value, digits = 2) {
  const num = toNumber(value);
  if (num == null) return null;
  return `${num.toFixed(digits)}%`;
}

function formatDateLabel(value) {
  const formatted = formatFlightDateShort(value);
  return formatted === "—" ? "" : formatted;
}

function formatDatePresetLabel(value) {
  const preset = String(value || "").trim().toLowerCase();
  const labels = {
    last_7d: "Last 7 days",
    last_14d: "Last 14 days",
    last_30d: "Last 30 days",
    last_60d: "Last 60 days",
    last_90d: "Last 90 days",
    active_campaigns: "Active campaigns",
  };
  return labels[preset] || "";
}

function formatWindowLabel(timeframe = {}) {
  const { start, end, datePreset } = timeframe || {};
  if (start && end) {
    return formatFlightRange(start, end);
  }

  if (start) return `Flight starts ${formatFlightDate(start)}`;
  if (end) return `Flight ends ${formatFlightDate(end)}`;

  return formatDatePresetLabel(datePreset) || null;
}

// Display-only descriptor for the resolved analysis window. Approved flight
// dates are the campaign source of truth; a trailing preset (Last 30 days) is
// only ever an explicit fallback or a user-selected manual filter.
export function describeAnalysisWindow(timeframe = {}) {
  const source = String(timeframe?.source || "").trim().toLowerCase();
  const datePreset = String(timeframe?.datePreset || "").trim().toLowerCase();
  const startDate = timeframe?.start || null;
  const endDate = timeframe?.end || null;
  const rangeLabel = startDate && endDate ? formatFlightRange(startDate, endDate) : "";

  if (timeframe?.isExplicitOverride === true && source !== "live_window_recovery") {
    const manualLabel = rangeLabel || formatDatePresetLabel(datePreset);
    return {
      mode: "manual",
      startDate,
      endDate,
      label: manualLabel ? `${manualLabel} (user-selected)` : "User-selected window",
      subtext: "Manual date filter selected by the user — not the approved flight default.",
    };
  }

  if (rangeLabel && source.startsWith("imported_reporting")) {
    // Read-only imported reporting campaign (created externally, e.g. in Meta
    // Ads Manager). Deliberately never labeled "Approved flight".
    const importedPlatform = String(timeframe?.importedReportingPlatform || "").trim();
    const provenance = String(timeframe?.importedReportingProvenance || "")
      .trim()
      .toLowerCase();
    const provenanceNote =
      provenance === "live_ads_read_when_approved"
        ? " Live Meta reporting reads use the connected ad account under the approved ads_read permission."
        : provenance === "recorded_test_campaign"
        ? " Recorded delivery data is shown for review/demo reporting; live Meta reads remain permission-gated until ads_read approval."
        : "";
    return {
      mode: "imported_reporting_flight",
      startDate,
      endDate,
      label: `Imported ${importedPlatform || "external"} campaign flight (${rangeLabel})`,
      subtext: `Read-only reporting surface for a campaign created in ${
        importedPlatform ? `${importedPlatform} Ads Manager` : "an external ads platform"
      } — Cphere did not create or activate this campaign.${provenanceNote}`,
    };
  }

  if (rangeLabel && source.startsWith("advertiser_reporting_window")) {
    /*
     * ALL CAMPAIGNS. Never "Approved flight": this window spans a portfolio of
     * campaigns with different flights, and it reaches the reporting cutoff
     * rather than any one campaign's end date.
     */
    return {
      mode: "advertiser_reporting_window",
      startDate,
      endDate,
      label: `All campaigns (${rangeLabel})`,
      subtext:
        "Reporting covers every campaign eligible for this advertiser, through the latest reported day.",
    };
  }

  if (
    rangeLabel &&
    (source.startsWith("approved_flight") || source.startsWith("advertiser_flight"))
  ) {
    return {
      mode: "approved_flight",
      startDate,
      endDate,
      label: `Approved flight (${rangeLabel})`,
      subtext: "Reporting is aligned to the approved campaign flight dates.",
    };
  }

  if (rangeLabel && datePreset === "flight") {
    return {
      mode: "approved_flight",
      startDate,
      endDate,
      label: `Campaign flight (${rangeLabel})`,
      subtext: "Reporting is aligned to the resolved campaign flight dates.",
    };
  }

  if (rangeLabel && source === "reported_window") {
    // A governed window with dates but nothing naming it: the dates ARE the
    // scope. Deliberately not routed into the preset fallback below, which
    // would claim flight dates are pending when none were being resolved.
    return {
      mode: "reported_window",
      startDate,
      endDate,
      label: rangeLabel,
      subtext: "Reporting covers the window these numbers were reported for.",
    };
  }

  if (rangeLabel && source === "live_window_recovery") {
    return {
      mode: "recovered_reporting_window",
      startDate,
      endDate,
      label: `${formatDatePresetLabel(datePreset) || "Reporting window"} (${rangeLabel}) — recovery`,
      subtext: "The requested window has no usable reporting. These metrics cover the recovered reporting period.",
    };
  }

  const presetLabel = formatDatePresetLabel(datePreset);
  if (presetLabel) {
    return {
      mode: "fallback_last_30_days",
      startDate,
      endDate,
      label: `${presetLabel} fallback — flight dates pending`,
      subtext: "No approved campaign flight dates are available for this advertiser yet.",
    };
  }

  const partialLabel = formatWindowLabel(timeframe);
  return {
    mode: "missing_dates",
    startDate,
    endDate,
    label: partialLabel || null,
    subtext: partialLabel ? "Approved flight dates are incomplete for this campaign." : null,
  };
}

// Keep selection context and the period that produced the metrics together in
// cached results. Renderers must never infer a value's dates from the selector.
function reportingWindowRecord(timeframe = {}) {
  return {
    start: timeframe.start || null,
    end: timeframe.end || null,
    datePreset: timeframe.datePreset || null,
    key: timeframe.key || null,
    source: timeframe.source || null,
    isExplicitOverride: timeframe.isExplicitOverride === true,
    timeZone: timeframe.timeZone || REPORTING_TIME_ZONE,
  };
}

function pickCanonicalTopPlatform(byChannel) {
  return chooseTopPlatform(byChannel || {});
}

function buildCanonicalToplineResult({
  canonicalPayload,
  normalizedAdvertiserId,
  advertiserName,
  campaign,
  requestedTimeframe = null,
}) {
  const canonicalSummary = canonicalPayload?.canonicalSummary || canonicalPayload;
  const canonicalContract = String(
    canonicalSummary?.contract ||
      canonicalSummary?.canonical_reporting_contract ||
      canonicalSummary?.sourceContract?.contract ||
      ""
  )
    .trim()
    .toLowerCase();
  const metrics = canonicalSummary?.aggregateTotals || canonicalSummary?.metrics || {};
  if (
    canonicalContract !== "canonical_reporting_v1" ||
    !canonicalSummary ||
    !Object.keys(metrics).length ||
    canonicalSummary.scope !== "active_campaigns"
  ) {
    return null;
  }

  const timeframe = canonicalSummary.timeframe || {};
  const requestedReportingWindow = reportingWindowRecord(requestedTimeframe || timeframe);
  const effectiveReportingWindow = reportingWindowRecord(timeframe);
  const requestedAnalysisWindow = describeAnalysisWindow(requestedTimeframe || timeframe);
  const flightWindow = canonicalSummary.flightWindow || null;
  const source = canonicalSummary.source || {};
  const pricing = canonicalSummary.pricing || {};
  const spendEfficiency = resolveSpendEfficiencyContract(canonicalSummary);
  const spendEfficiencyMetric = resolveSpendEfficiencyMetric(spendEfficiency, {
    platformKey: "youtube",
    metricKey: "cpqv",
  });
  const completedViewCostMetric = resolveSpendEfficiencyMetric(spendEfficiency, {
    platformKey: "youtube",
    metricKey: "cpcv",
  });

  const spendRaw = toNumber(metrics.spend) ?? 0;
  const impressionsRaw = toNumber(metrics.impressions) ?? 0;
  const clicksRaw = toNumber(metrics.clicks) ?? 0;
  const conversionsRaw = toNumber(metrics.conversions) ?? 0;
  const engagementsRaw = toNumber(metrics.engagements) ?? 0;
  const videoViewsRaw =
    toNumber(
      metrics.trueViewViews ??
        metrics.true_view_views ??
        metrics.video_views ??
        metrics.videoViews
    ) ?? 0;
  const videoStartsRaw = toNumber(metrics.video_started ?? metrics.videoStarted) ?? 0;
  const videoCompletionsRaw =
    toNumber(
      metrics.videoCompletions ??
        metrics.video_completions ??
        metrics.video_completed ??
        metrics.videoCompleted ??
        metrics.video_complete ??
        metrics.completedViews ??
        metrics.completed_views ??
        metrics.completeViews ??
        metrics.views100 ??
        metrics.videoViews100 ??
        metrics.quartile_100 ??
        metrics.q100
    ) ?? 0;
  const videoPlayed25Raw = toNumber(metrics.videoPlayed25 ?? metrics.video_played_25);
  const videoPlayed50Raw = toNumber(metrics.videoPlayed50 ?? metrics.video_played_50);
  const videoPlayed75Raw = toNumber(metrics.videoPlayed75 ?? metrics.video_played_75);
  const vvrRaw =
    toNumber(metrics.videoViewRate ?? metrics.video_view_rate) ??
    (impressionsRaw > 0 ? (videoViewsRaw / impressionsRaw) * 100 : null);
  const cpqvRaw = toNumber(
    spendEfficiencyMetric?.value ??
      metrics.costPerQualifiedVideoView
  );
  const cpcvRaw = toNumber(completedViewCostMetric?.value);
  const freshnessEvidence = canonicalSummary?.dataFreshness || null;
  const sourceReadAt =
    freshnessEvidence?.timestamp ||
    freshnessEvidence?.lastSourceReadAt ||
    freshnessEvidence?.sourceUpdatedAt ||
    source.timestamp ||
    null;
  const dataFreshnessMinutes = sourceReadAt
    ? Math.max(0, (Date.now() - new Date(sourceReadAt).getTime()) / 60000)
    : null;
  const ctrRaw =
    deriveCrossChannelCtrPercent(canonicalSummary.byChannel) ??
    deriveCtrPercentFromClicks({ clicks: clicksRaw, impressions: impressionsRaw });
  const cpcRaw =
    toNumber(metrics.cpc) ??
    (clicksRaw > 0 ? spendRaw / clicksRaw : null);
  const cpmRaw =
    toNumber(metrics.cpm) ??
    (impressionsRaw > 0 ? (spendRaw / impressionsRaw) * 1000 : null);
  const cpaRaw =
    toNumber(metrics.cpa) ??
    (conversionsRaw > 0 ? spendRaw / conversionsRaw : null);
  /* Canonical completion rate WINS: the engine pins its denominator to
     video starts and pairs evidence per channel; the local totals division
     is only the fallback for payloads carrying no canonical rate. */
  const vcrRaw =
    toNumber(metrics.videoCompletionRate ?? metrics.completionRate) ??
    (videoStartsRaw > 0 ? videoCompletionsRaw / videoStartsRaw : null);

  const topPlatform = pickCanonicalTopPlatform(canonicalSummary.byChannel);
  const pacing = buildPacingStatus({ campaign, spend: spendRaw, flightRange: flightWindow });
  const costLabel = cpaRaw != null ? "CPA" : "CPC";
  const costValue = cpaRaw != null ? cpaRaw : cpcRaw;
  const canonicalFlightRange = normalizeFlightRange(flightWindow);
  /*
   * The window is named from governed scope, never from timeframe.label. That
   * label is a request-origin note the reporting spine stamps for diagnostics
   * ("Optimus platform tab window (...)"), it is not keyed into the summary
   * cache, and it reached the Delivery chart's subtitle, axis and tooltip
   * verbatim. It is preserved below as provenance and rendered nowhere.
   */
  const analysisWindow = describeAnalysisWindow(
    resolveGovernedReportingWindowScope({
      requestedTimeframe,
      reportedTimeframe: timeframe,
      flightWindow: canonicalFlightRange,
    })
  );
  const windowLabel = analysisWindow.label;
  const resolvedAdvertiserName =
    advertiserName || canonicalSummary?.advertiser?.name || "";

  return {
    spend: formatCurrency(spendRaw) || null,
    impressions: formatInteger(impressionsRaw) || null,
    ctr: formatPercentScale(ctrRaw) || null,
    cpc_or_cpa: formatCurrency(costValue, 2) || null,
    cpm: formatCurrency(cpmRaw, 2) || null,
    clicks: formatInteger(clicksRaw) || null,
    conversions: formatInteger(conversionsRaw) || null,
    videoViews: formatInteger(videoViewsRaw) || null,
    videoStarts: formatInteger(videoStartsRaw) || null,
    videoCompletions: formatInteger(videoCompletionsRaw) || null,
    vcr: formatPercent(vcrRaw) || null,
    costLabel,
    topPlatform,
    pacingStatus: pacing.label,
    pacingMeta: pacing.meta,
    pacingTone: pacing.tone,
    insightSummary: buildFallbackInsight({
      advertiserName: resolvedAdvertiserName,
      topPlatform,
      pacingStatus: pacing.label,
      spend: spendRaw,
      ctr: ctrRaw,
      gaSessions: null,
      impressions: impressionsRaw,
      clicks: clicksRaw,
      conversions: conversionsRaw,
      engagements: engagementsRaw,
      videoViews: videoViewsRaw,
    }),
    scopeLabel: resolvedAdvertiserName || "Advertiser active campaigns",
    windowLabel,
    analysisWindow,
    requestedWindowLabel: requestedAnalysisWindow.label,
    requestedAnalysisWindow,
    raw: {
      requestedReportingWindow,
      effectiveReportingWindow,
      // The calendar the response states its days in (null when it states
      // none) — what a surface labels, never the portal's own default.
      reportingTimeZone: responseReportingTimeZone(canonicalSummary),
      reportingCalendar: canonicalSummary?.reportingCalendar || null,
      /*
       * Provenance is kept, off the label. Diagnostics and auditing still read
       * which caller and which basis produced this window; no renderer does.
       */
      reportingWindowProvenance: {
        requestLabel: timeframe.requestLabel || timeframe.label || null,
        basis: timeframe.basis || null,
        source: timeframe.source || null,
      },
      spend: spendRaw,
      impressions: impressionsRaw,
      ctr: ctrRaw,
      cpc: cpcRaw,
      cpm: cpmRaw,
      cpa: cpaRaw,
      clicks: clicksRaw,
      conversions: conversionsRaw,
      engagements: engagementsRaw,
      videoViews: videoViewsRaw,
      trueViewViews: videoViewsRaw,
      videoStarts: videoStartsRaw,
      videoCompletions: videoCompletionsRaw,
      youtubeVideoCompletions: videoCompletionsRaw,
      videoPlayed25: videoPlayed25Raw,
      videoPlayed50: videoPlayed50Raw,
      videoPlayed75: videoPlayed75Raw,
      vcr: vcrRaw,
      vvr: vvrRaw,
      cpqv: cpqvRaw,
      cpcv: cpcvRaw,
      costPerQualifiedVideoView: spendEfficiencyMetric,
      costPerCompletedView: completedViewCostMetric,
      spendEfficiency,
      dataFreshness: Number.isFinite(dataFreshnessMinutes) ? dataFreshnessMinutes : null,
      dataFreshnessEvidence: freshnessEvidence,
      sessions: null,
      gaConversions: null,
      topPlatform,
      pacingStatus: pacing.label,
      snapshotSource: source.label || null,
      snapshotSourceTier: source.tier || null,
      snapshotSourceLabel: source.label || null,
      snapshotSourceConfidence: source.confidence != null ? Number(source.confidence) : null,
      snapshotSourceTimestamp: sourceReadAt,
      sourceDataThrough: canonicalSummary?.dataFreshness?.dataThrough || null,
      sourceFreshnessStatus: canonicalSummary?.dataFreshness?.freshness || null,
      sourceFailureReason: canonicalSummary?.dataFreshness?.failureReason || null,
      snapshotSourceUrl: null,
      snapshotSourceNotes: Array.isArray(source.notes) ? source.notes : [],
      snapshotSourceLimitations: Array.isArray(source.limitations) ? source.limitations : [],
      snapshotSourceDimensions: null,
      snapshotIsLive: source.isLive === true,
      snapshotIsFallback: source.isFallback === true,
      snapshotFound: true,
      snapshotUsedFallback: source.isFallback === true,
      snapshotResolverVersion: canonicalSummary.version || null,
      snapshotDataSource: "canonical_active_client_billed_summary",
      campaignId: null,
      platformKey: "all",
      toplineResolverVersion: CONNECTED_PLATFORM_TOPLINE_VERSION,
      spendBasis: spendEfficiency?.spendBasis || "GROSS_CLIENT_BILLED",
      /*
       * Client vocabulary only. The old default hardcoded the internal basis
       * label whenever the server omitted one — which is exactly what a
       * client-safe server payload does.
       */
      spendBasisClientLabel:
        resolveClientSpendBasisLabel(spendEfficiency) || "Client-billed",
      pricingApplied: Array.isArray(pricing.appliedPlatforms) && pricing.appliedPlatforms.length > 0,
      pricingModel:
        Array.isArray(pricing.appliedPlatforms) && pricing.appliedPlatforms.length > 0
          ? "mixed_active_campaign_pricing"
          : null,
      pricingLineItemKey:
        Array.isArray(pricing.appliedPlatforms) && pricing.appliedPlatforms.length > 0
          ? "active_campaigns"
          : null,
      pricingAppliedPlatforms: pricing.appliedPlatforms || [],
      pricingFallbackPlatforms: pricing.fallbackPlatforms || [],
      mediaSpend: null,
      mediaCpm: null,
      providerSpend: null,
      providerCpm: null,
      clientFacingSpend: spendRaw,
      clientFacingCpm: cpmRaw,
      billedSpend: spendRaw,
      billedCpm: cpmRaw,
      datePreset: "active_campaigns",
      startDate: timeframe.start || null,
      endDate: timeframe.end || null,
      timeframeKey: `active_campaigns:${timeframe.start || ""}:${timeframe.end || ""}`,
      requestedDatePreset: requestedReportingWindow.datePreset,
      requestedStartDate: requestedReportingWindow.start,
      requestedEndDate: requestedReportingWindow.end,
      requestedTimeframeKey: requestedReportingWindow.key,
      requestedTimeframeSource: requestedReportingWindow.source || "canonical_dashboard_welcome_summary",
      recoveredFromAutoFlightWindow: false,
      flightWindow,
      flightStart: flightWindow?.start || null,
      flightEnd: flightWindow?.end || null,
      hasAnyData: true,
      usableDataFound: true,
      advertiserId: normalizedAdvertiserId,
      sourceContract: {
        contract: canonicalContract,
        scope: canonicalSummary.scope,
        spendBasis: canonicalSummary.spendBasis,
        pricingBasis: canonicalSummary.pricingBasis,
        version: canonicalSummary.version,
      },
    },
  };
}

/*
 * Canonical platform projection used by the dedicated YouTube page. This is
 * intentionally sourced from dashboard/performance-summary — the same object
 * Platform Analytics reads — so the dedicated route has no second resolver,
 * no latest-snapshot fallback, and no frontend arithmetic that can drift.
 */
export function buildCanonicalPlatformToplineResult({
  canonicalPayload,
  normalizedAdvertiserId,
  normalizedPlatformKey,
  advertiserName = "",
  campaign = null,
  requestedCampaignId = null,
  requestedTimeframe = null,
} = {}) {
  const canonicalSummary = canonicalPayload?.canonicalSummary || canonicalPayload;
  const canonicalContract = String(
    canonicalSummary?.contract ||
      canonicalSummary?.canonical_reporting_contract ||
      canonicalSummary?.sourceContract?.contract ||
      ""
  )
    .trim()
    .toLowerCase();
  if (
    canonicalContract !== "canonical_reporting_v1" ||
    !canonicalSummary ||
    !["active_campaigns", "campaign"].includes(canonicalSummary.scope) ||
    !normalizedPlatformKey
  ) {
    return null;
  }

  const platform =
    canonicalSummary.platforms?.[normalizedPlatformKey] ||
    canonicalSummary.channelBreakdown?.[normalizedPlatformKey] ||
    null;
  const channel = canonicalSummary.byChannel?.[normalizedPlatformKey] || null;
  const metrics = platform?.metrics || channel || {};
  const isYoutube = normalizedPlatformKey === "youtube";
  const timeframe = canonicalSummary.timeframe || {};
  const requestedReportingWindow = reportingWindowRecord(requestedTimeframe || timeframe);
  const effectiveReportingWindow = reportingWindowRecord(timeframe);
  const requestedAnalysisWindow = describeAnalysisWindow(requestedTimeframe || timeframe);
  const flightWindow = canonicalSummary.flightWindow || null;
  const source = canonicalSummary.source || {};
  const pricing = canonicalSummary.pricing || {};
  const freshness = canonicalSummary.dataFreshness || {};
  const spendEfficiency = resolveSpendEfficiencyContract(canonicalSummary, {
    platformKey: normalizedPlatformKey,
  });
  const spendEfficiencyMetric = resolveSpendEfficiencyMetric(spendEfficiency, {
    platformKey: normalizedPlatformKey,
    metricKey: "cpqv",
  });
  const completedViewCostMetric = resolveSpendEfficiencyMetric(spendEfficiency, {
    platformKey: normalizedPlatformKey,
    metricKey: "cpcv",
  });

  const spendRaw = toNumber(
    platform?.clientFacingSpend ??
      channel?.clientFacingSpend ??
      channel?.client_facing_spend ??
      metrics.clientFacingSpend ??
      metrics.client_facing_spend ??
      metrics.billedSpend ??
      metrics.billed_spend ??
      metrics.spend
  );
  const impressionsRaw = toNumber(metrics.impressions);
  const clicksRaw = toNumber(metrics.clicks);
  const conversionsRaw = toNumber(metrics.conversions);
  const engagementsRaw = toNumber(metrics.engagements);
  /* YouTube has one governed denominator: TrueView views. Generic videoViews
     is deliberately not a fallback for this platform. The canonical channel
     aggregate wins over the route-local platform bag so a stale legacy alias
     cannot mask the promoted sheet-derived count. */
  const videoViewsRaw = isYoutube
    ? toNumber(
        channel?.trueViewViews ??
          channel?.true_view_views ??
          metrics.trueViewViews ??
          metrics.true_view_views
      )
    : toNumber(metrics.videoViews ?? metrics.video_views);
  const videoStartsRaw = toNumber(metrics.videoStarts ?? metrics.video_started);
  const videoCompletionsRaw = toNumber(
    channel?.videoCompletions100 ??
      channel?.youtubeVideoCompletions ??
      channel?.video_completed ??
      channel?.videoCompletions ??
      metrics.videoCompletions100 ??
      metrics.youtubeVideoCompletions ??
      metrics.videoCompletions ??
      metrics.video_completions ??
      metrics.video_completed
  );
  const videoPlayed25Raw = toNumber(metrics.videoPlayed25 ?? metrics.video_played_25);
  const videoPlayed50Raw = toNumber(metrics.videoPlayed50 ?? metrics.video_played_50);
  const videoPlayed75Raw = toNumber(metrics.videoPlayed75 ?? metrics.video_played_75);
  const ctrRaw =
    toNumber(metrics.ctr) ??
    (impressionsRaw != null
      ? deriveCtrPercentFromClicks({ clicks: clicksRaw, impressions: impressionsRaw })
      : null);
  const vvrRaw =
    toNumber(metrics.videoViewRate ?? metrics.video_view_rate) ??
    (impressionsRaw > 0 && videoViewsRaw != null
      ? (videoViewsRaw / impressionsRaw) * 100
      : null);
  const cpqvRaw = toNumber(
    spendEfficiencyMetric?.value ?? metrics.costPerQualifiedVideoView
  );
  const cpcvRaw = toNumber(completedViewCostMetric?.value);
  const cpcRaw =
    toNumber(metrics.cpc) ??
    (spendRaw != null && clicksRaw > 0 ? spendRaw / clicksRaw : null);
  const cpmRaw =
    toNumber(metrics.cpm) ??
    (spendRaw != null && impressionsRaw > 0 ? (spendRaw / impressionsRaw) * 1000 : null);
  const cpaRaw =
    toNumber(metrics.cpa) ??
    (spendRaw != null && conversionsRaw > 0 ? spendRaw / conversionsRaw : null);
  const vcrRaw =
    videoStartsRaw > 0 && videoCompletionsRaw != null
      ? videoCompletionsRaw / videoStartsRaw
      : null;
  const providerSpendRaw =
    toNumber(platform?.providerSpend ?? channel?.providerSpend ?? channel?.provider_spend) ?? null;
  const observedDeliveryValues = [
    spendRaw,
    impressionsRaw,
    clicksRaw,
    engagementsRaw,
    videoViewsRaw,
    videoCompletionsRaw,
  ];
  // A raw-column presence claim from this canonical channel distinguishes a
  // measured zero window from absent metrics normalized to historical zeros.
  // It does not change positive-performance semantics or metric validation.
  const reportedZero =
    (channel?.hasReportedDeliveryData === true || metrics.hasReportedDeliveryData === true) &&
    ([metrics.spend, metrics.clientFacingSpend, metrics.client_facing_spend,
      metrics.billedSpend, metrics.billed_spend, metrics.impressions, metrics.clicks,
      metrics.engagements, metrics.trueViewViews, metrics.true_view_views,
      metrics.videoViews, metrics.video_views, metrics.videoCompletions,
      metrics.video_completed, metrics.video_completions].some((value) =>
      (typeof value === "number" || (typeof value === "string" && value.trim() !== "")) &&
      toNumber(value) === 0
    ) || readReportedCompletionCount(metrics.videoPlayedTo100Count) === 0);
  const hasAnyData = reportedZero || observedDeliveryValues.some(
    (value) => typeof value === "number" && Number.isFinite(value) && value > 0
  );
  const costLabel = cpaRaw != null ? "CPA" : "CPC";
  const costValue = cpaRaw != null ? cpaRaw : cpcRaw;
  const resolvedAdvertiserName = advertiserName || canonicalSummary?.advertiser?.name || "";
  const platformLabel = normalizePlatformLabel(normalizedPlatformKey);
  const pacing = buildPacingStatus({ campaign, spend: spendRaw, flightRange: flightWindow });
  const canonicalFlightRange = normalizeFlightRange(flightWindow);
  const platformAnalysisWindow = describeAnalysisWindow(
    resolveGovernedReportingWindowScope({
      requestedTimeframe,
      reportedTimeframe: timeframe,
      flightWindow: canonicalFlightRange,
    })
  );
  const sourceReadAt =
    freshness.lastSourceReadAt || freshness.sourceUpdatedAt || source.timestamp || null;
  const dataFreshnessMinutes = sourceReadAt
    ? Math.max(0, (Date.now() - new Date(sourceReadAt).getTime()) / 60000)
    : null;
  const pricingAppliedPlatforms = Array.isArray(pricing.appliedPlatforms)
    ? pricing.appliedPlatforms
    : [];
  const pricingFallbackPlatforms = Array.isArray(pricing.fallbackPlatforms)
    ? pricing.fallbackPlatforms
    : [];

  return {
    spend: spendRaw == null ? null : formatCurrency(spendRaw),
    impressions: impressionsRaw == null ? null : formatInteger(impressionsRaw),
    ctr: ctrRaw == null ? null : formatPercentScale(ctrRaw),
    cpc_or_cpa: costValue == null ? null : formatCurrency(costValue, 2),
    cpm: cpmRaw == null ? null : formatCurrency(cpmRaw, 2),
    clicks: clicksRaw == null ? null : formatInteger(clicksRaw),
    conversions: conversionsRaw == null ? null : formatInteger(conversionsRaw),
    videoViews: videoViewsRaw == null ? null : formatInteger(videoViewsRaw),
    videoStarts: videoStartsRaw == null ? null : formatInteger(videoStartsRaw),
    videoCompletions:
      videoCompletionsRaw == null ? null : formatInteger(videoCompletionsRaw),
    vcr: vcrRaw == null ? null : formatPercent(vcrRaw),
    costLabel,
    topPlatform: platformLabel,
    pacingStatus: pacing.label,
    pacingMeta: pacing.meta,
    pacingTone: pacing.tone,
    insightSummary: hasAnyData
      ? buildFallbackInsight({
          advertiserName: resolvedAdvertiserName,
          topPlatform: platformLabel,
          pacingStatus: pacing.label,
          spend: spendRaw,
          ctr: ctrRaw,
          gaSessions: null,
          impressions: impressionsRaw,
          clicks: clicksRaw,
          conversions: conversionsRaw,
          engagements: engagementsRaw,
          videoViews: videoViewsRaw,
        })
      : null,
    scopeLabel: `${platformLabel} Analytics`,
    windowLabel: platformAnalysisWindow.label,
    analysisWindow: platformAnalysisWindow,
    requestedWindowLabel: requestedAnalysisWindow.label,
    requestedAnalysisWindow,
    raw: {
      requestedReportingWindow,
      effectiveReportingWindow,
      // The calendar the response states its days in (null when it states
      // none) — what a surface labels, never the portal's own default.
      reportingTimeZone: responseReportingTimeZone(canonicalSummary),
      reportingCalendar: canonicalSummary?.reportingCalendar || null,
      reportingWindowProvenance: {
        requestLabel: timeframe.requestLabel || timeframe.label || null,
        basis: timeframe.basis || null,
        source: timeframe.source || null,
      },
      spend: spendRaw,
      impressions: impressionsRaw,
      ctr: ctrRaw,
      cpc: cpcRaw,
      cpm: cpmRaw,
      cpa: cpaRaw,
      clicks: clicksRaw,
      conversions: conversionsRaw,
      engagements: engagementsRaw,
      videoViews: isYoutube ? null : videoViewsRaw,
      trueViewViews: isYoutube ? videoViewsRaw : null,
      videoStarts: videoStartsRaw,
      videoCompletions: videoCompletionsRaw,
      videoPlayedTo100Count: readReportedCompletionCount(channel?.videoPlayedTo100Count ?? metrics.videoPlayedTo100Count),
      ...readYoutubeQuartileRates(channel?.youtubeQuartileRates ? channel : metrics),
      videoCompletions100: isYoutube ? videoCompletionsRaw : null,
      youtubeVideoCompletions: isYoutube ? videoCompletionsRaw : null,
      videoPlayed25: videoPlayed25Raw,
      videoPlayed50: videoPlayed50Raw,
      videoPlayed75: videoPlayed75Raw,
      vcr: vcrRaw,
      vvr: vvrRaw,
      cpqv: cpqvRaw,
      cpcv: cpcvRaw,
      costPerQualifiedVideoView: spendEfficiencyMetric,
      costPerCompletedView: completedViewCostMetric,
      spendEfficiency,
      sessions: null,
      topPlatform: platformLabel,
      dataFreshness: Number.isFinite(dataFreshnessMinutes) ? dataFreshnessMinutes : null,
      sourceDataThrough: freshness.dataThrough || null,
      sourceFreshnessStatus: freshness.freshness || null,
      sourceFailureReason: freshness.failureReason || null,
      sourceLastReadAt: freshness.lastSourceReadAt || null,
      sourceRowCount: freshness.sourceRowCount ?? null,
      sourceHash: freshness.sourceHash || null,
      sourceVersion: freshness.sourceVersion || null,
      sourceWorkbookTitle: freshness.sourceWorkbookTitle || freshness.youtube?.sourceWorkbookTitle || null,
      sourceWorksheet: freshness.sourceWorksheet || freshness.youtube?.sourceWorksheet || null,
      sourceSheetId: freshness.sourceSheetId ?? freshness.youtube?.sourceSheetId ?? null,
      snapshotSource: source.label || null,
      snapshotSourceTier: source.tier || null,
      snapshotSourceLabel: source.label || null,
      snapshotSourceTimestamp: sourceReadAt,
      snapshotIsLive: source.isLive === true,
      snapshotIsFallback: source.isFallback === true,
      snapshotFound: hasAnyData,
      snapshotUsedFallback: source.isFallback === true,
      snapshotResolverVersion: canonicalSummary.version || null,
      snapshotDataSource: "canonical_active_client_billed_platform",
      campaignId: requestedCampaignId || null,
      platformKey: normalizedPlatformKey,
      toplineResolverVersion: CONNECTED_PLATFORM_TOPLINE_VERSION,
      spendBasis: spendEfficiency?.spendBasis || canonicalSummary.spendBasis || "GROSS_CLIENT_BILLED",
      /*
       * Client vocabulary only. The old default hardcoded the internal basis
       * label whenever the server omitted one — which is exactly what a
       * client-safe server payload does.
       */
      spendBasisClientLabel:
        resolveClientSpendBasisLabel(spendEfficiency) || "Client-billed",
      pricingApplied: pricingAppliedPlatforms.includes(normalizedPlatformKey),
      pricingModel: platform?.pricingModel || channel?.pricingModel || null,
      pricingSource: platform?.pricingSource || channel?.pricingSource || null,
      pricingBasis: canonicalSummary.pricingBasis || null,
      markupPercent: platform?.markupPercent ?? channel?.markupPercent ?? null,
      pricingAppliedPlatforms,
      pricingFallbackPlatforms,
      mediaSpend: providerSpendRaw,
      providerSpend: providerSpendRaw,
      clientFacingSpend: spendRaw,
      clientFacingCpm: cpmRaw,
      billedSpend: spendRaw,
      billedCpm: cpmRaw,
      datePreset: requestedTimeframe?.datePreset || "active_campaigns",
      startDate: timeframe.start || requestedTimeframe?.start || null,
      endDate: timeframe.end || requestedTimeframe?.end || null,
      requestedDatePreset: requestedReportingWindow.datePreset,
      requestedStartDate: requestedReportingWindow.start,
      requestedEndDate: requestedReportingWindow.end,
      requestedTimeframeKey: requestedReportingWindow.key,
      requestedTimeframeSource: requestedReportingWindow.source || "canonical_dashboard_platform_summary",
      recoveredFromAutoFlightWindow: false,
      flightWindow,
      flightStart: flightWindow?.start || null,
      flightEnd: flightWindow?.end || null,
      dailyDelivery: resolveCanonicalDailyDeliveryForPlatform(
        canonicalSummary,
        normalizedPlatformKey
      ),
      siteVisits: resolveObservedSiteVisits(canonicalSummary?.siteVisits),
      hasAnyData,
      usableDataFound: hasAnyData,
      advertiserId: normalizedAdvertiserId,
      sourceContract: {
        contract: canonicalContract,
        scope: canonicalSummary.scope,
        spendBasis: canonicalSummary.spendBasis,
        pricingBasis: canonicalSummary.pricingBasis,
        version: canonicalSummary.version,
      },
    },
  };
}

function diffDaysInclusive(start, end) {
  const startDate = toDate(start);
  const endDate = toDate(end);
  if (!startDate || !endDate) return null;
  const a = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
  const b = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
  return Math.round((b.getTime() - a.getTime()) / 86400000) + 1;
}

function clampDateOnlyToRange(value, min, max) {
  const dateOnly = String(value || "").slice(0, 10);
  if (!dateOnly) return null;
  if (min && dateOnly < min) return min;
  if (max && dateOnly > max) return max;
  return dateOnly;
}

function normalizeTimeframe(timeframe) {
  if (typeof timeframe === "string" && timeframe.trim()) {
    return {
      datePreset: timeframe.trim(),
      start: null,
      end: null,
      key: timeframe.trim(),
      source: "explicit_preset",
      isExplicitOverride: true,
    };
  }

  const start = String(timeframe?.start || timeframe?.dateStart || "").slice(0, 10) || null;
  const end = String(timeframe?.end || timeframe?.dateEnd || "").slice(0, 10) || null;
  const datePreset = String(timeframe?.datePreset || "").trim();
  const explicitSource = String(timeframe?.source || "").trim().toLowerCase();
  const explicitOverride =
    timeframe?.isExplicitOverride === true ||
    explicitSource === "explicit_user" ||
    explicitSource === "explicit_preset";

  if (datePreset) {
    return {
      datePreset,
      start,
      end,
      key: `${datePreset}:${start || ""}:${end || ""}`,
      source: explicitSource || (explicitOverride ? "explicit_preset" : "default_preset"),
      isExplicitOverride: explicitOverride,
    };
  }

  if (start && end) {
    const days = diffDaysInclusive(start, end);
    let inferredPreset = "last_30d";
    if (days != null && days <= 7) inferredPreset = "last_7d";
    else if (days != null && days <= 14) inferredPreset = "last_14d";
    else if (days != null && days <= 30) inferredPreset = "last_30d";
    else if (days != null && days <= 60) inferredPreset = "last_60d";
    else if (days != null && days <= 90) inferredPreset = "last_90d";

    return {
      datePreset: inferredPreset,
      start,
      end,
      key: `${inferredPreset}:${start}:${end}`,
      source: explicitOverride ? explicitSource || "explicit_user" : "explicit_user",
      isExplicitOverride: true,
    };
  }

  return {
    datePreset: "last_30d",
    start: null,
    end: null,
    key: "last_30d",
    source: "default_preset",
    isExplicitOverride: false,
  };
}

function extractCampaignFlightRange(campaign) {
  const start =
    parseCampaignDate(campaign?.flight_start_date) ||
    parseCampaignDate(campaign?.flightStart) ||
    parseCampaignDate(campaign?.start_date) ||
    null;
  const end =
    parseCampaignDate(campaign?.flight_end_date) ||
    parseCampaignDate(campaign?.flightEnd) ||
    parseCampaignDate(campaign?.end_date) ||
    null;

  return start && end ? { start, end } : null;
}

function normalizeFlightRange(value) {
  const start = String(value?.start || value?.dateStart || "").slice(0, 10) || null;
  const end = String(value?.end || value?.dateEnd || "").slice(0, 10) || null;
  return start && end ? { start, end } : null;
}

function resolveFlightRangeForPacing({ campaign, advertiserFlightRange, timeframe } = {}) {
  return (
    extractCampaignFlightRange(campaign) ||
    normalizeFlightRange(advertiserFlightRange) ||
    (String(timeframe?.datePreset || "").toLowerCase() === "flight"
      ? normalizeFlightRange(timeframe)
      : null)
  );
}

// Read-only external reporting campaigns (imported Meta Ads Manager campaigns
// recorded for ads_read reporting). They are never approved/launchable and
// never gate anything; they only provide an honest flight window + provenance
// for the analytics display when no approved Cphere campaign exists.
export function isReportingOnlyExternalCampaign(campaign) {
  if (!campaign || typeof campaign !== "object") return false;
  if (String(campaign.status || "").trim().toUpperCase() === "REPORTING_ONLY") return true;
  const rawPayload = campaign.strategy_payload ?? campaign.strategyPayload;
  let payload = rawPayload;
  if (typeof rawPayload === "string") {
    try {
      payload = JSON.parse(rawPayload);
    } catch {
      payload = null;
    }
  }
  if (!payload || typeof payload !== "object") return false;
  if (payload.reportingOnly === true) return true;
  return Boolean(
    payload.externalReporting &&
      typeof payload.externalReporting === "object" &&
      payload.externalReporting.readOnly === true
  );
}

export function selectReportingOnlyExternalCampaign(campaigns) {
  const rows = (Array.isArray(campaigns) ? campaigns : []).filter(
    isReportingOnlyExternalCampaign
  );
  if (!rows.length) return null;
  const withFlight = rows.filter((row) => extractCampaignFlightRange(row));
  const pool = withFlight.length ? withFlight : rows;
  return pool
    .slice()
    .sort((a, b) => {
      const at = a?.updated_at ? new Date(a.updated_at).getTime() : 0;
      const bt = b?.updated_at ? new Date(b.updated_at).getTime() : 0;
      return bt - at;
    })[0];
}

// Client-visible campaign lists hide internal reporting-only rows from
// non-superadmin viewers, but the analytics surfaces still need the imported
// flight window + provenance to label recorded delivery honestly. The
// advertiser-scoped context endpoint returns only that client-safe context;
// merge it back in whenever the campaign list carries no reporting-only row.
export const IMPORTED_REPORTING_CONTEXT_PATH =
  "/api/media-campaigns/imported-reporting-context";

export function mergeImportedReportingContextCampaigns(campaigns, contextPayload) {
  const list = Array.isArray(campaigns) ? campaigns : [];
  if (selectReportingOnlyExternalCampaign(list)) return list;
  const imported = (
    Array.isArray(contextPayload?.importedReportingCampaigns)
      ? contextPayload.importedReportingCampaigns
      : []
  ).filter(isReportingOnlyExternalCampaign);
  return imported.length ? [...list, ...imported] : list;
}

function getImportedReportingContext(campaign) {
  const rawPayload = campaign?.strategy_payload ?? campaign?.strategyPayload;
  let payload = rawPayload;
  if (typeof rawPayload === "string") {
    try {
      payload = JSON.parse(rawPayload);
    } catch {
      payload = null;
    }
  }
  const external =
    payload && typeof payload === "object" && typeof payload.externalReporting === "object"
      ? payload.externalReporting
      : {};
  const platformKey = String(external?.platform || "").trim().toLowerCase();
  return {
    platformLabel: platformKey === "meta" ? "Meta" : platformKey ? normalizePlatformLabel(platformKey) : "",
    provenance: String(external?.provenance || "").trim().toLowerCase(),
  };
}

/*
 * The Meta parent scope. Meta delivery is recorded per publisher platform, and
 * the governed Meta total is the sum of every one of them — this is the same
 * decomposition the backend applies in metaAdsReadLibraryService's
 * PLATFORM_POLICY / SEPARATELY_GOVERNED_PUBLISHER_PLATFORMS, so the Meta page
 * reconciles to Meta's account-level total rather than to a subset of it.
 *
 * This list is NOT a request for `channel=meta`. The snapshot endpoint
 * canonicalizes "meta" to "facebook", so a Meta page that asked for its own
 * key would silently render Facebook-only delivery under a Meta label. The
 * parent view is therefore assembled by fanning out across the child channels
 * and summing them, never by naming the parent.
 */
export const META_PARENT_REPORTING_CHANNELS = Object.freeze([
  "facebook",
  "instagram",
  "audience_network",
  "messenger",
]);

// Meta-family recorded reporting aggregation. Recorded Meta delivery is stored
// per publisher platform (facebook, instagram, audience_network, messenger).
// Audience Network has no page of its own, so its recorded delivery reports
// with Facebook; Instagram keeps instagram-only rows so its cards never
// inherit Facebook/Audience Network delivery. The Meta parent takes the whole
// family. Scoped strictly to imported reporting windows for the child pages so
// every other advertiser/page keeps single-channel behavior.
export function resolveImportedMetaReportingChannels(requestedChannel) {
  const key = String(requestedChannel || "").trim().toLowerCase();
  if (key === "meta") return [...META_PARENT_REPORTING_CHANNELS];
  if (key === "facebook") return ["facebook", "audience_network"];
  if (key === "instagram") return ["instagram"];
  if (key === "audience_network") return ["audience_network"];
  return null;
}

/*
 * The Meta parent aggregates on every window, not only on imported reporting
 * windows. A child page defaults to its single channel unless an imported Meta
 * campaign splits its delivery; the parent page has no single channel to fall
 * back to, so fanning out is the only way it can be correct at all.
 */
export function resolveMetaParentReportingChannels(requestedPlatformKey) {
  return String(requestedPlatformKey || "").trim().toLowerCase() === "meta"
    ? [...META_PARENT_REPORTING_CHANNELS]
    : null;
}

const META_FAMILY_CHANNEL_LABELS = {
  facebook: "Facebook",
  instagram: "Instagram",
  audience_network: "Audience Network",
  messenger: "Messenger",
};

function mergeMetaFamilyRecordedSnapshots(channelKeys, settledResults) {
  const resolved = channelKeys.map((channelKey, index) => {
    const result = settledResults[index];
    return {
      channel: channelKey,
      payload: result && result.status === "fulfilled" ? result.value : null,
    };
  });
  const found = resolved.filter(
    (entry) => entry.payload?.found === true && entry.payload?.kpis && typeof entry.payload.kpis === "object"
  );
  if (!found.length) return resolved[0]?.payload || null;

  const combined = {};
  for (const entry of found) {
    for (const [key, value] of Object.entries(entry.payload.kpis)) {
      if (key === "ctr" || key === "cpm" || key === "hasReportedDeliveryData") continue;
      if (typeof value !== "number" && (typeof value !== "string" || !value.trim())) continue;
      const numeric = Number(value);
      if (!Number.isFinite(numeric)) continue;
      combined[key] = (combined[key] || 0) + numeric;
    }
  }
  /*
   * Combined CTR is eligibility-scoped through the byte-pinned metricBasis
   * engine — every Meta-family placement is click inventory, so this
   * normally equals the summed ratio, but a member that reports no click
   * evidence must fall out of the basis rather than be read as zero. CPM
   * stays whole-pool (every impression has a cost).
   */
  const combinedCtrBasis = aggregateMetricBasis(
    "ctr",
    found.map((entry) => ({
      platformKey: entry.channel,
      metrics: sanitizeSnapshotMetrics(entry.payload.kpis || {}),
    }))
  );
  if (combinedCtrBasis?.value != null) {
    combined.ctr = combinedCtrBasis.value;
  }
  if (combined.spend != null && combined.impressions > 0) {
    combined.cpm = (combined.spend / combined.impressions) * 1000;
  }

  const primary = found.reduce(
    (best, entry) =>
      !best || (readCanonicalMetricAlias(sanitizeSnapshotMetrics(entry.payload.kpis), "spend") ?? 0) >
        (readCanonicalMetricAlias(sanitizeSnapshotMetrics(best.payload.kpis), "spend") ?? 0)
        ? entry
        : best,
    null
  );
  // The parent scope is the whole family; a child scope is any narrower slice.
  const isMetaParentScope = META_PARENT_REPORTING_CHANNELS.every((channelKey) =>
    channelKeys.includes(channelKey)
  );
  const channels = found.map((entry) => ({
    channel: entry.channel,
    label: META_FAMILY_CHANNEL_LABELS[entry.channel] || entry.channel,
    spend: readCanonicalMetricAlias(sanitizeSnapshotMetrics(entry.payload.kpis), "spend"),
    impressions: readCanonicalMetricAlias(sanitizeSnapshotMetrics(entry.payload.kpis), "impressions"),
    clicks: readCanonicalMetricAlias(sanitizeSnapshotMetrics(entry.payload.kpis), "clicks"),
  }));

  return {
    ...primary.payload,
    found: true,
    kpis: combined,
    metaFamilyRecordedAggregation: {
      channels,
      isMetaParentScope,
      note: isMetaParentScope
        ? `Meta totals combine ${found
            .map((entry) => META_FAMILY_CHANNEL_LABELS[entry.channel] || entry.channel)
            .join(", ")} delivery, so they reconcile to Meta rather than to any single publisher platform.`
        : channelKeys.includes("facebook") && channelKeys.includes("audience_network")
        ? "Facebook reporting includes Audience Network delivery for the imported campaign window."
        : channelKeys.length === 1 && channelKeys[0] === "instagram"
        ? "Instagram reporting is shown separately from Facebook and Audience Network delivery."
        : `Recorded Meta delivery for ${found
            .map((entry) => META_FAMILY_CHANNEL_LABELS[entry.channel] || entry.channel)
            .join(", ")} in the imported campaign window.`,
    },
  };
}

export function resolvePreferredAnalysisCampaign(campaigns, { selectedCampaignName = null } = {}) {
  const approvedCampaign = selectApprovedPlanCampaign({
    campaigns,
    selectedCampaignName,
    allowAdvertiserBestFit: false,
    allowSingleApprovedFallback: true,
  });
  if (approvedCampaign) return approvedCampaign;
  return selectBestCampaign(campaigns);
}

/*
 * The date preset for an ADVERTISER-scoped reporting window. It is deliberately
 * not "flight": a portfolio of campaigns with different flights has no single
 * flight, and labelling one as such is how a completed campaign's end date came
 * to be printed as the advertiser's reporting cutoff.
 */
export const ADVERTISER_REPORTING_WINDOW_PRESET = "reporting_window";

/*
 * The canonical All-Campaigns window as `advertiser_reporting_scope_v1` states
 * it. Accepts the scope object or a bare {start,end} so a caller may pass
 * either the payload or the window it already unwrapped.
 */
function normalizeAdvertiserReportingWindow(input) {
  const window = input?.window && typeof input.window === "object" ? input.window : input;
  const start = String(window?.start || "").slice(0, 10);
  const end = String(window?.end || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(start) || !/^\d{4}-\d{2}-\d{2}$/.test(end)) return null;
  return start <= end ? { start, end } : null;
}

export function resolveDefaultAnalysisTimeframe({
  timeframe,
  campaigns,
  selectedCampaignName = null,
  advertiserFlightRange = null,
  advertiserReportingWindow = null,
  nowMs = Date.now(),
} = {}) {
  const normalizedTimeframe = normalizeTimeframe(timeframe);
  const approvedCampaign = selectApprovedPlanCampaign({
    campaigns,
    selectedCampaignName,
    nowMs,
    allowAdvertiserBestFit: true,
    allowSingleApprovedFallback: true,
  });
  const approvedFlightRange = extractCampaignFlightRange(approvedCampaign);
  const advertiserFlight =
    advertiserFlightRange?.start && advertiserFlightRange?.end
      ? {
          start: String(advertiserFlightRange.start).slice(0, 10),
          end: String(advertiserFlightRange.end).slice(0, 10),
        }
      : null;
  /*
   * ALL CAMPAIGNS is an ADVERTISER scope, so its window is the advertiser's —
   * the canonical `advertiser_reporting_scope_v1` window the backend resolves
   * from the whole eligible portfolio and the reporting cutoff.
   *
   * It used to be one campaign's flight. `selectApprovedPlanCampaign` returns a
   * SINGLE campaign and scores a countersignature (+200) above being in flight
   * (+100), so for an advertiser whose countersigned campaign had ENDED while a
   * sibling was still delivering, the ended campaign's flight end became the
   * advertiser's reporting cutoff. Every campaign stayed in scope — the campaign
   * set was never wrong — but the days after that flight closed were outside the
   * window, and the estate panel reported an advertiser total that stopped
   * before the advertiser did.
   *
   * A selected campaign still governs its own window: single-campaign scope must
   * stay isolated, and the portfolio window would leak sibling delivery into it.
   */
  const portfolioWindow = selectedCampaignName
    ? null
    : normalizeAdvertiserReportingWindow(advertiserReportingWindow);
  const flightRange = portfolioWindow || approvedFlightRange || advertiserFlight;
  const flightSourcePrefix = portfolioWindow
    ? "advertiser_reporting_window"
    : approvedFlightRange
    ? "approved_flight"
    : "advertiser_flight";
  const defaultDatePreset = portfolioWindow
    ? ADVERTISER_REPORTING_WINDOW_PRESET
    : "flight";

  if (normalizedTimeframe.isExplicitOverride) {
    if (normalizedTimeframe.source === "live_window_recovery") {
      return normalizedTimeframe;
    }
    if (flightRange?.start && flightRange?.end) {
      if (normalizedTimeframe.start && normalizedTimeframe.end) {
        if (
          normalizedTimeframe.end < flightRange.start ||
          normalizedTimeframe.start > flightRange.end
        ) {
          return {
            datePreset: defaultDatePreset,
            start: flightRange.start,
            end: flightRange.end,
            key: `${defaultDatePreset}:${flightRange.start}:${flightRange.end}`,
            source: `${flightSourcePrefix}_enforced`,
            isExplicitOverride: false,
          };
        }

        const clampedStart = clampDateOnlyToRange(
          normalizedTimeframe.start,
          flightRange.start,
          flightRange.end
        );
        const clampedEnd = clampDateOnlyToRange(
          normalizedTimeframe.end,
          flightRange.start,
          flightRange.end
        );

        if (clampedStart && clampedEnd && clampedStart <= clampedEnd) {
          return {
            ...normalizedTimeframe,
            start: clampedStart,
            end: clampedEnd,
            key: `${normalizedTimeframe.datePreset || "custom"}:${clampedStart}:${clampedEnd}`,
            source:
              clampedStart !== normalizedTimeframe.start ||
              clampedEnd !== normalizedTimeframe.end
                ? `${flightSourcePrefix}_clamped`
                : normalizedTimeframe.source,
          };
        }
      }

      return {
        datePreset: defaultDatePreset,
        start: flightRange.start,
        end: flightRange.end,
        key: `${defaultDatePreset}:${flightRange.start}:${flightRange.end}`,
        source: `${flightSourcePrefix}_enforced`,
        isExplicitOverride: false,
      };
    }

    return normalizedTimeframe;
  }

  if (flightRange?.start && flightRange?.end) {
    return {
      datePreset: defaultDatePreset,
      start: flightRange.start,
      end: flightRange.end,
      key: `${defaultDatePreset}:${flightRange.start}:${flightRange.end}`,
      source: `${flightSourcePrefix}_default`,
      isExplicitOverride: false,
    };
  }

  // No approved Cphere flight anywhere: a read-only imported reporting
  // campaign (created externally, e.g. in Meta Ads Manager) may still provide
  // an honest reporting window. It is display/reporting context only — it is
  // NOT an approved flight and never becomes one.
  const importedCampaign = selectReportingOnlyExternalCampaign(campaigns);
  const importedFlightRange = extractCampaignFlightRange(importedCampaign);
  if (importedFlightRange?.start && importedFlightRange?.end) {
    const importedContext = getImportedReportingContext(importedCampaign);
    return {
      datePreset: "flight",
      start: importedFlightRange.start,
      end: importedFlightRange.end,
      key: `imported_flight:${importedFlightRange.start}:${importedFlightRange.end}`,
      source: "imported_reporting_flight_default",
      isExplicitOverride: false,
      importedReportingPlatform: importedContext.platformLabel,
      importedReportingProvenance: importedContext.provenance,
    };
  }

  return normalizedTimeframe;
}

function normalizePlatformLabel(value) {
  const key = String(value || "").trim().toLowerCase();
  return PLATFORM_LABELS[key] || String(value || "").trim() || "Cross-Platform";
}

function normalizePlatformArray(value) {
  if (Array.isArray(value)) return value.filter(Boolean).map((item) => String(item));
  if (typeof value === "string" && value.trim()) {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
}

function normalizeProgrammaticKey(value) {
  const normalized = String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "");
  if (!normalized) return "";
  if (normalized === "in_game") return "ingame";
  return normalized;
}

// Wave 3: identity-only lookup. Post-strip pricing summaries carry only
// lineItemKey/lineItemLabel/channel/provider labels — no pricing objects, no
// pricing models. This resolver matches on identity alone and is used purely
// for line-item labeling, never for financial math.
function findProgrammaticPricingIdentity(campaign, platformKey) {
  const normalizedPlatformKey = normalizeProgrammaticKey(platformKey);
  if (!normalizedPlatformKey) return null;

  const summary = Array.isArray(campaign?.programmatic_pricing_summary)
    ? campaign.programmatic_pricing_summary
    : [];

  for (const item of summary) {
    const normalizedLineItemKey = normalizeProgrammaticKey(
      item?.lineItemKey ?? item?.line_item_key
    );
    const normalizedChannel = normalizeProgrammaticKey(item?.channel);

    if (
      normalizedLineItemKey === normalizedPlatformKey ||
      normalizedChannel === normalizedPlatformKey
    ) {
      return item;
    }
  }

  return null;
}

/**
 * Wave 3 contract: the browser-side billing engine is retired. Client-billed
 * spend is computed SERVER-side from governed commercial terms; the client
 * never recomputes billed values from provider figures plus pricing
 * configuration (pricing config is no longer served to client tokens).
 *
 * This resolver only reads already-served billed fields
 * (`billedSpend`/`billedCost`/`clientFacingSpend`, plus served
 * `billedCpm`/`billedCpc`/`billedCpa` where present) off the metrics bag it is
 * given, and returns the historical shape built ONLY from those served values
 * — or null when the server declared no billed basis. Provider figures are
 * never served to client tokens, so `providerSpend`/`providerCpm` are null.
 */
export function computeClientProgrammaticCommercialMetrics({
  campaign,
  platformKey,
  metrics,
  impressions,
  clicks,
  conversions,
}) {
  const servedMetrics = metrics && typeof metrics === "object" ? metrics : null;
  const servedBilledCost = toNumber(
    servedMetrics?.billedSpend ??
      servedMetrics?.billedCost ??
      servedMetrics?.clientFacingSpend ??
      null
  );

  if (servedBilledCost == null) return null;

  const deliveredImpressions = toNumber(impressions ?? servedMetrics?.impressions);
  const deliveredClicks = toNumber(clicks ?? servedMetrics?.clicks);
  const deliveredConversions = toNumber(conversions ?? servedMetrics?.conversions);

  const servedBilledCpm = toNumber(
    servedMetrics?.billedCpm ?? servedMetrics?.clientFacingCpm ?? null
  );
  const servedBilledCpc = toNumber(servedMetrics?.billedCpc ?? null);
  const servedBilledCpa = toNumber(servedMetrics?.billedCpa ?? null);

  const effectiveBilledCpm =
    servedBilledCpm != null
      ? servedBilledCpm
      : deliveredImpressions != null && deliveredImpressions > 0
      ? (servedBilledCost / deliveredImpressions) * 1000
      : null;
  const effectiveBilledCpc =
    servedBilledCpc != null
      ? servedBilledCpc
      : deliveredClicks != null && deliveredClicks > 0
      ? servedBilledCost / deliveredClicks
      : null;
  const effectiveBilledCpa =
    servedBilledCpa != null
      ? servedBilledCpa
      : deliveredConversions != null && deliveredConversions > 0
      ? servedBilledCost / deliveredConversions
      : null;

  const identity = findProgrammaticPricingIdentity(campaign, platformKey);
  const servedBasis = String(servedMetrics?.spendBasis || "")
    .trim()
    .toLowerCase();

  return {
    pricingModel: servedBasis || "server_billed",
    lineItemKey:
      identity?.line_item_key ||
      identity?.lineItemKey ||
      normalizeProgrammaticKey(platformKey),
    billedCost: servedBilledCost,
    billedCpm: effectiveBilledCpm,
    billedCpc: effectiveBilledCpc,
    billedCpa: effectiveBilledCpa,
    providerSpend: null,
    providerCpm: null,
  };
}

// Wave 3 contract: per-platform client-facing spend is read ONLY from the
// server-declared billed fields (`billedSpend`/`clientFacingSpend`) each
// platform row already carries. There is no browser-side pricing math and no
// silent provider/net (`metrics.spend`) fallback — when the server declared no
// billed basis for a platform, that platform contributes delivery counts but
// NO spend, and it is recorded in `pricingFallbackPlatforms` ("server declared
// no basis") for presentation bookkeeping.
function resolveAggregateClientFacingMetrics({ snapshot }) {
  const byPlatform =
    snapshot?.byPlatform && typeof snapshot.byPlatform === "object"
      ? snapshot.byPlatform
      : null;

  if (!byPlatform || !Object.keys(byPlatform).length) return null;

  const totals = {
    billedSpend: 0,
    impressions: 0,
    clicks: 0,
    conversions: 0,
    engagements: 0,
    videoViews: 0,
    videoStarts: 0,
    videoCompletions: 0,
  };
  let billedObserved = false;
  const rankedPlatforms = [];
  const eligibleRateRows = [];
  const pricingAppliedPlatforms = [];
  const pricingFallbackPlatforms = [];

  Object.entries(byPlatform).forEach(([rawPlatformKey, platformMetrics]) => {
    const platformKey = String(rawPlatformKey || "").trim().toLowerCase();
    const metrics =
      platformMetrics && typeof platformMetrics === "object" ? platformMetrics : null;
    if (!platformKey || !metrics) return;

    const impressions = toNumber(metrics.impressions);
    const clicks = toNumber(metrics.clicks);
    const conversions = toNumber(metrics.conversions);
    const engagements = toNumber(metrics.engagements);
    const videoViews = toNumber(metrics.videoViews ?? metrics.video_views);
    const videoStarts = toNumber(
      metrics.videoStarts ??
        metrics.video_started ??
        metrics.videoStarted ??
        metrics.pageStarts ??
        metrics.page_starts
    );
    const videoCompletions = toNumber(
      metrics.videoCompletions ??
        metrics.video_completed ??
        metrics.videoCompleted ??
        metrics.completed_views
    );
    // Served-billed-or-null. NEVER metrics.spend: a row without a declared
    // billed basis stays null (fail closed), it does not inherit a
    // provider-basis figure.
    const billedSpend = toNumber(
      metrics.billedSpend ?? metrics.clientFacingSpend ?? null
    );
    const hasSignal = hasPositiveSignal(
      billedSpend,
      impressions,
      clicks,
      conversions,
      engagements,
      videoViews,
      videoStarts,
      videoCompletions
    );
    if (!hasSignal) return;

    if (billedSpend != null) {
      billedObserved = true;
      totals.billedSpend += billedSpend;
      pricingAppliedPlatforms.push(platformKey);
    } else {
      // Delivery exists but the server declared no billed basis for this
      // platform in this window.
      pricingFallbackPlatforms.push(platformKey);
    }

    totals.impressions += impressions || 0;
    totals.clicks += clicks || 0;
    totals.conversions += conversions || 0;
    totals.engagements += engagements || 0;
    totals.videoViews += videoViews || 0;
    totals.videoStarts += videoStarts || 0;
    totals.videoCompletions += videoCompletions || 0;

    /*
     * Per-platform evidence for the eligibility-scoped rate basis: a
     * platform whose clicks are unsupported (CTV/YouTube) contributes its
     * billed spend to the total but never to cost-per-click, and completion
     * evidence pairs per platform. Null evidence stays null here — missing
     * is not zero.
     */
    eligibleRateRows.push({
      platformKey,
      metrics: {
        spend: billedSpend,
        impressions,
        clicks,
        videoViews,
        engagements,
        videoStarts,
        videoCompletions,
      },
    });

    rankedPlatforms.push({
      platformKey,
      billedSpend: billedSpend || 0,
      impressions: impressions || 0,
    });
  });

  if (!rankedPlatforms.length) return null;

  rankedPlatforms.sort((a, b) => {
    if (b.billedSpend !== a.billedSpend) return b.billedSpend - a.billedSpend;
    return b.impressions - a.impressions;
  });

  const billedSpendTotal = billedObserved ? totals.billedSpend : null;

  return {
    topPlatform: normalizePlatformLabel(rankedPlatforms[0]?.platformKey || ""),
    billedSpend: billedSpendTotal,
    providerSpend: null,
    impressions: totals.impressions,
    clicks: totals.clicks,
    conversions: totals.conversions,
    engagements: totals.engagements,
    videoViews: totals.videoViews,
    videoStarts: totals.videoStarts,
    videoCompletions: totals.videoCompletions,
    billedCpm:
      billedSpendTotal != null && totals.impressions > 0
        ? (billedSpendTotal / totals.impressions) * 1000
        : null,
    /*
     * Blended CPC and completion rate are eligibility-scoped (byte-pinned
     * metricBasis): click-ineligible spend never enters CPC, and completions
     * divide only their own platforms' start evidence (STARTS-pinned).
     * CPM is eligibility-classified but supports every platform (an
     * impression always has a cost), so its basis is the whole delivering
     * pool; CPA is SEMANTIC and is withheld across incompatible sources
     * rather than blended — neither is "whole-pool by classification".
     */
    billedCpc: aggregateMetricBasis("cpc", eligibleRateRows)?.value ?? null,
    billedCpa:
      billedSpendTotal != null && totals.conversions > 0
        ? billedSpendTotal / totals.conversions
        : null,
    /* vcr is consumed as a ratio (0-1); the engine returns percent. */
    vcr: (() => {
      const scoped = aggregateMetricBasis("completionRate", eligibleRateRows)?.value;
      return scoped == null ? null : scoped / 100;
    })(),
    pricingApplied: pricingAppliedPlatforms.length > 0,
    pricingAppliedPlatforms,
    pricingFallbackPlatforms,
  };
}

function parseCampaignDate(value) {
  if (!value) return null;
  const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : null;
}

function selectBestCampaign(campaigns) {
  const rows = Array.isArray(campaigns) ? campaigns : [];
  if (!rows.length) return null;

  const now = new Date();

  const enriched = rows.map((campaign) => {
    const start =
      parseCampaignDate(campaign?.flight_start_date) ||
      parseCampaignDate(campaign?.flightStart) ||
      parseCampaignDate(campaign?.start_date);
    const end =
      parseCampaignDate(campaign?.flight_end_date) ||
      parseCampaignDate(campaign?.flightEnd) ||
      parseCampaignDate(campaign?.end_date);

    const updatedAt =
      toDate(campaign?.updated_at) ||
      toDate(campaign?.updatedAt) ||
      toDate(campaign?.created_at) ||
      toDate(campaign?.createdAt) ||
      null;

    const startDate = toDate(start);
    const endDate = toDate(end);
    const isActive =
      startDate && endDate ? now >= startDate && now <= endDate : false;
    const isUpcoming = startDate ? now < startDate : false;

    return {
      campaign,
      start,
      end,
      startDate,
      endDate,
      updatedAt,
      isActive,
      isUpcoming,
    };
  });

  const active = enriched
    .filter((entry) => entry.isActive)
    .sort((a, b) => {
      const aEnd = a.endDate ? a.endDate.getTime() : 0;
      const bEnd = b.endDate ? b.endDate.getTime() : 0;
      return bEnd - aEnd;
    });
  if (active.length) return active[0].campaign;

  const upcoming = enriched
    .filter((entry) => entry.isUpcoming)
    .sort((a, b) => {
      const aStart = a.startDate ? a.startDate.getTime() : Infinity;
      const bStart = b.startDate ? b.startDate.getTime() : Infinity;
      return aStart - bStart;
    });
  if (upcoming.length) return upcoming[0].campaign;

  return enriched
    .sort((a, b) => {
      const aUpdated = a.updatedAt ? a.updatedAt.getTime() : 0;
      const bUpdated = b.updatedAt ? b.updatedAt.getTime() : 0;
      return bUpdated - aUpdated;
    })[0]?.campaign || null;
}

function chooseTopPlatform(byPlatform, fallbackPlatforms = []) {
  const entries = Object.entries(byPlatform || {})
    .map(([key, metrics]) => ({
      key,
      label: normalizePlatformLabel(key),
      spend: toNumber(metrics?.spend) || 0,
      impressions: toNumber(metrics?.impressions) || 0,
      clicks: toNumber(metrics?.clicks) || 0,
      conversions: toNumber(metrics?.conversions) || 0,
    }))
    .sort((a, b) => {
      if (b.spend !== a.spend) return b.spend - a.spend;
      if (b.conversions !== a.conversions) return b.conversions - a.conversions;
      if (b.impressions !== a.impressions) return b.impressions - a.impressions;
      return b.clicks - a.clicks;
    });

  if (entries.length && (entries[0].spend > 0 || entries[0].impressions > 0 || entries[0].clicks > 0)) {
    return entries[0].label;
  }

  if (Array.isArray(fallbackPlatforms) && fallbackPlatforms.length) {
    return normalizePlatformLabel(fallbackPlatforms[0]);
  }

  return null;
}

// A flight boundary's calendar day: an ISO date prefix as written; a Date (a
// DATEONLY value delivered as a UTC-midnight instant) by its UTC date.
function flightDayKey(value) {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value.toISOString().slice(0, 10);
  }
  const match = String(value || "").trim().match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : null;
}

function buildPacingStatus({ campaign, spend, flightRange = null } = {}) {
  const campaignFlight = extractCampaignFlightRange(campaign);
  const fallbackFlight = normalizeFlightRange(flightRange);
  const start = campaignFlight?.start || fallbackFlight?.start || null;
  const end = campaignFlight?.end || fallbackFlight?.end || null;

  // Flight dates are calendar days compared with the Eastern reporting day.
  // (They were device-local noons, so "Flight ended" appeared half a day
  // early, and elapsed days counted from the UTC date.)
  const startDay = toDate(start) ? flightDayKey(start) : null;
  const endDay = toDate(end) ? flightDayKey(end) : null;
  const today = reportingToday();

  if (!startDay || !endDay) {
    return { label: null, tone: "watch", meta: "No active campaign flight" };
  }

  if (today < startDay) {
    return { label: "Pre-flight", tone: "watch", meta: `${formatDateLabel(start)} start` };
  }

  if (today > endDay) {
    return { label: "Flight ended", tone: "watch", meta: `${formatDateLabel(end)} end` };
  }

  const totalBudget =
    toNumber(campaign?.total_budget) ||
    toNumber(campaign?.budget) ||
    toNumber(campaign?.totalBudget) ||
    null;

  if (!totalBudget || !spend) {
    return { label: "In flight", tone: "strong", meta: `${formatDateLabel(start)} - ${formatDateLabel(end)}` };
  }

  const totalDays = diffDaysInclusive(start, end);
  const elapsedDays = diffDaysInclusive(start, today);
  if (!totalDays || !elapsedDays) {
    return { label: "In flight", tone: "strong", meta: `${formatDateLabel(start)} - ${formatDateLabel(end)}` };
  }

  const elapsedShare = Math.min(1, Math.max(0, elapsedDays / totalDays));
  const expectedSpend = totalBudget * elapsedShare;
  const deltaPct = totalBudget > 0 ? ((spend - expectedSpend) / totalBudget) * 100 : 0;

  if (deltaPct >= 8) {
    return { label: "Ahead of plan", tone: "strong", meta: `${deltaPct.toFixed(1)}% over pace` };
  }

  if (deltaPct <= -8) {
    return { label: "Behind plan", tone: "risk", meta: `${Math.abs(deltaPct).toFixed(1)}% under pace` };
  }

  return { label: "On pace", tone: "strong", meta: `${Math.abs(deltaPct).toFixed(1)}% variance` };
}

function buildFallbackInsight({
  advertiserName,
  topPlatform,
  pacingStatus,
  spend,
  ctr,
  gaSessions,
  impressions,
  clicks,
  conversions,
  engagements,
  videoViews,
}) {
  const hasSignal = [
    spend,
    ctr,
    gaSessions,
    impressions,
    clicks,
    conversions,
    engagements,
    videoViews,
  ].some((value) => {
    const numeric = toNumber(value);
    return numeric != null && numeric > 0;
  });

  if (!hasSignal) {
    return "Awaiting data sync.";
  }

  const lines = [];
  if (topPlatform) lines.push(`${topPlatform} is currently leading delivery`);
  if (spend) lines.push(`spend is ${formatCurrency(spend)}`);
  if (!spend && impressions) lines.push(`impressions have reached ${formatInteger(impressions)}`);
  if (!spend && clicks) lines.push(`clicks have reached ${formatInteger(clicks)}`);
  if (ctr != null) lines.push(`CTR is ${formatPercentScale(ctr)}`);
  if (conversions) lines.push(`tracked conversions are ${formatInteger(conversions)}`);
  if (engagements) lines.push(`engagements are ${formatInteger(engagements)}`);
  if (videoViews) lines.push(`video views are ${formatInteger(videoViews)}`);
  if (gaSessions) lines.push(`GA4 is showing ${formatInteger(gaSessions)} sessions`);

  const base = lines.length
    ? `${lines.join(", ")} for ${advertiserName || "this advertiser"}.`
    : `Performance is syncing for ${advertiserName || "this advertiser"}.`;

  if (!pacingStatus) return base;
  return `${base} Pacing is currently ${String(pacingStatus).toLowerCase()}.`;
}

function buildMetricSignature(payload) {
  if (!payload || typeof payload !== "object") return "";
  const signatureFields = [
    "platformKey",
    "spend",
    "impressions",
    "clicks",
    "ctr",
    "cpc",
    "cpm",
    "cpa",
    "conversions",
    "engagements",
    "videoViews",
    "videoStarts",
    "videoCompletions",
    "vcr",
    "sessions",
    "topPlatform",
    "pacingStatus",
  ];
  const normalized = {};
  signatureFields.forEach((field) => {
    const value = payload[field];
    normalized[field] =
      typeof value === "number" && Number.isFinite(value)
        ? Number(value.toFixed(6))
        : value ?? null;
  });
  return JSON.stringify(normalized);
}

export async function getToplinePerformance({
  advertiserId,
  timeframe,
  advertiserFlightRange = null,
  platformKey = "",
  campaignId = null,
  campaignName = null,
  signal,
  applyProgrammaticCampaignPricing = true,
} = {}) {
  const normalizedAdvertiserId = Number(advertiserId);
  if (!normalizedAdvertiserId) return null;

  const normalizedPlatformKey = String(platformKey || "").trim().toLowerCase();
  const snapshotChannel = normalizedPlatformKey || "all";
  const shouldTracePlatformRequest =
    CONNECTED_PLATFORM_REQUEST_DEBUG_KEYS.has(normalizedPlatformKey);
  const tracePlatformRequest = (stage, payload = {}) => {
    if (!shouldTracePlatformRequest) return;
    logPlatformRequestDebug(stage, {
      app: "client",
      advertiserId: normalizedAdvertiserId,
      channel: snapshotChannel,
      platformKey: normalizedPlatformKey,
      backendBaseUrl: API_BASE_URL || null,
      backendBaseUrlSource: API_BASE_URL
        ? process.env.REACT_APP_BACKEND_URL
          ? "REACT_APP_BACKEND_URL"
          : "production_default"
        : "window_origin_or_dev_proxy",
      windowOrigin: getWindowOrigin(),
      nodeEnv: process.env.NODE_ENV || null,
      ...payload,
    });
  };

  const campaignQuery = new URLSearchParams({
    advertiserId: String(normalizedAdvertiserId),
  });
  const campaignPath = `/api/media-campaigns?${campaignQuery.toString()}`;

  tracePlatformRequest("campaign_request", {
    requestUrl: apiUrl(campaignPath),
    inputTimeframe: timeframe || null,
    advertiserFlightRange: advertiserFlightRange || null,
  });

  const importedContextPath = `${IMPORTED_REPORTING_CONTEXT_PATH}?${campaignQuery.toString()}`;
  const campaignsResult = await Promise.allSettled([
    fetchJson(campaignPath, { signal }),
    fetchJson(importedContextPath, { signal }),
  ]);
  const campaignsPayload =
    campaignsResult[0]?.status === "fulfilled" ? campaignsResult[0].value : null;
  const listedCampaigns = Array.isArray(campaignsPayload?.campaigns)
    ? campaignsPayload.campaigns
    : Array.isArray(campaignsPayload)
    ? campaignsPayload
    : [];
  const campaigns = mergeImportedReportingContextCampaigns(
    listedCampaigns,
    campaignsResult[1]?.status === "fulfilled" ? campaignsResult[1].value : null
  );
  const selection = resolveReportingCampaignSelection({ campaigns, advertiserId: normalizedAdvertiserId, campaignId, campaignName });
  if (selection.unavailable) return null;
  const selectedCampaign = selection.campaign;
  const campaign = selectedCampaign || resolvePreferredAnalysisCampaign(campaigns);
  const snapshotCampaignId = selection.id;
  const hasRequestedCampaign = Boolean(
    String(campaignId || "").trim() || String(campaignName || "").trim()
  );
  /* The campaign selector already resolved this campaign's governed flight.
     Re-running advertiser-level best-fit selection here could clamp campaign
     two back onto campaign one's dates. */
  const normalizedTimeframe = hasRequestedCampaign
    ? normalizeTimeframe(timeframe)
    : resolveDefaultAnalysisTimeframe({ timeframe, campaigns, advertiserFlightRange });
  // Neither endpoint can establish a one-sided explicit reporting window.
  if (Boolean(normalizedTimeframe.start) !== Boolean(normalizedTimeframe.end)) return null;
  const selectedCampaignPreset = String(normalizedTimeframe.datePreset || "").trim().toLowerCase();
  const effectiveSnapshotTimeframe = resolveEffectiveReportingTimeframe({
    timeframe: normalizedTimeframe, campaign: snapshotCampaignId != null ? selectedCampaign : null,
  });
  // The canonical endpoint has no preset parameter. Resolve selected preset-only
  // requests before calling it; explicit canonical dates remain verbatim.
  const canonicalTimeframe = snapshotCampaignId != null &&
    !normalizedTimeframe.start && !normalizedTimeframe.end &&
    ["flight", "full_delivery"].includes(selectedCampaignPreset)
      ? effectiveSnapshotTimeframe : normalizedTimeframe;
  if (snapshotCampaignId != null &&
      ["flight", "full_delivery"].includes(selectedCampaignPreset) &&
      !normalizedTimeframe.start && !normalizedTimeframe.end &&
      (!canonicalTimeframe.start || !canonicalTimeframe.end)) return null;
  const displayTimeframe = hasRequestedCampaign
    ? normalizedTimeframe
    : resolveDefaultAnalysisTimeframe({
        timeframe: null,
        campaigns,
        advertiserFlightRange,
      });
  const flightRangeForPacing = resolveFlightRangeForPacing({
    campaign,
    advertiserFlightRange,
    timeframe: normalizedTimeframe,
  });

  tracePlatformRequest("timeframe_resolved", {
    campaignsStatus: campaignsResult[0]?.status || null,
    campaignCount: campaigns.length,
    selectedCampaign: campaign
      ? {
          id: campaign.id || campaign.campaign_id || null,
          name: getCanonicalCampaignDisplayName(campaign) || null,
          status: campaign.status || null,
          flightStart: campaign.flight_start_date || campaign.flightStart || null,
          flightEnd: campaign.flight_end_date || campaign.flightEnd || null,
        }
      : null,
    normalizedTimeframe,
    displayTimeframe,
    advertiserFlightRange: advertiserFlightRange || null,
    flightRangeForPacing,
  });

  const shouldUseAggregateClientFacingSpend =
    applyProgrammaticCampaignPricing !== false &&
    (!normalizedPlatformKey || normalizedPlatformKey === "all");
  const shouldApplyProgrammaticCampaignPricing =
    applyProgrammaticCampaignPricing !== false &&
    PROGRAMMATIC_PRICING_CHANNEL_KEYS.has(normalizedPlatformKey);
  const requestedSpendBasis = "GROSS_CLIENT_BILLED";
  const canonicalCampaignKey = `${snapshotCampaignId ?? "all"}:${campaignName || ""}`;
  const cacheKey = `${normalizedAdvertiserId}:${normalizedTimeframe.key}:${snapshotChannel}:${canonicalCampaignKey}:${requestedSpendBasis}:${effectiveSnapshotTimeframe.key || ""}`;
  const cached = dataCache.get(cacheKey);
  if (cached && Date.now() - cached.ts < DATA_CACHE_TTL_MS) {
    tracePlatformRequest("cache_hit", {
      cacheKey,
      cacheAgeMs: Date.now() - cached.ts,
      cachedSnapshotSource: cached.value?.raw?.snapshotSource || null,
      cachedSnapshotSourceTier: cached.value?.raw?.snapshotSourceTier || null,
      cachedSnapshotFound: cached.value?.raw?.snapshotFound ?? null,
      cachedUsableDataFound: cached.value?.raw?.usableDataFound ?? null,
    });
    return cached.value;
  }

  const shouldUseCanonicalPerformanceSummary =
    shouldUseAggregateClientFacingSpend || normalizedPlatformKey === "youtube";
  if (shouldUseCanonicalPerformanceSummary) {
    try {
      const canonicalQuery = new URLSearchParams({
        advertiserId: String(normalizedAdvertiserId),
      });
      if (canonicalTimeframe.start) canonicalQuery.set("startDate", canonicalTimeframe.start);
      if (canonicalTimeframe.end) canonicalQuery.set("endDate", canonicalTimeframe.end);
      if (snapshotCampaignId != null) canonicalQuery.set("campaignId", String(snapshotCampaignId));
      const canonicalPayload = await fetchJson(
        `/api/dashboard/performance-summary?${canonicalQuery.toString()}`,
        { signal }
      );
      if (!canonicalReportingScopeMatches(canonicalPayload?.canonicalSummary || canonicalPayload, {
        advertiserId: normalizedAdvertiserId, campaignId: snapshotCampaignId, timeframe: canonicalTimeframe,
      })) throw new Error("Canonical reporting response does not match the requested scope.");
      const canonicalResult = normalizedPlatformKey === "youtube"
        ? buildCanonicalPlatformToplineResult({
            canonicalPayload,
            normalizedAdvertiserId,
            normalizedPlatformKey,
            advertiserName:
              campaign?.advertiser_name ||
              campaign?.advertiser ||
              canonicalPayload?.advertiser?.name ||
              canonicalPayload?.canonicalSummary?.advertiser?.name ||
              "",
            campaign,
            requestedCampaignId: snapshotCampaignId,
            requestedTimeframe: normalizedTimeframe,
          })
        : buildCanonicalToplineResult({
            canonicalPayload,
            normalizedAdvertiserId,
            advertiserName:
              campaign?.advertiser_name ||
              campaign?.advertiser ||
              canonicalPayload?.advertiser?.name ||
              canonicalPayload?.canonicalSummary?.advertiser?.name ||
              "",
            campaign,
            requestedTimeframe: normalizedTimeframe,
          });
      if (canonicalResult) {
        dataCache.set(cacheKey, { ts: Date.now(), value: canonicalResult });
        return canonicalResult;
      }
      if (normalizedPlatformKey === "youtube") {
        throw new Error("Canonical YouTube performance contract was unavailable for this scope.");
      }
    } catch (error) {
      /* YouTube fails closed on its one canonical contract. Falling through to
         Optimus would silently restore the latest-day legacy snapshot. */
      if (normalizedPlatformKey === "youtube") throw error;
    }
  }

  const buildSnapshotPathForChannel = (channelKey) => {
    const query = new URLSearchParams({
      advertiserId: String(normalizedAdvertiserId),
      channel: channelKey,
      datePreset: normalizedTimeframe.datePreset,
      allowLatestFallback:
        normalizedTimeframe.start && normalizedTimeframe.end ? "0" : "1",
    });
    if (snapshotCampaignId != null) query.set("campaignId", String(snapshotCampaignId));
    if (normalizedTimeframe.start) query.set("startDate", normalizedTimeframe.start);
    if (normalizedTimeframe.end) query.set("endDate", normalizedTimeframe.end);
    return `/api/optimus/snapshot?${query.toString()}`;
  };
  // Imported Meta reporting campaigns: recorded delivery is split across the
  // Meta publisher-platform channels. The Facebook page reports facebook +
  // audience_network together (Audience Network has no page of its own);
  // Instagram stays instagram-only so it never inherits the rest of the
  // Meta family.
  const importedMetaReportingChannels =
    normalizedTimeframe?.source === "imported_reporting_flight_default" &&
    String(normalizedTimeframe?.importedReportingPlatform || "").trim().toLowerCase() === "meta"
      ? resolveImportedMetaReportingChannels(normalizedPlatformKey)
      : null;
  // The Meta parent page fans out unconditionally — see
  // resolveMetaParentReportingChannels. Requesting `channel=meta` would be
  // canonicalized to Facebook by the snapshot endpoint and would report a
  // subset of Meta as though it were all of Meta.
  const metaParentReportingChannels = resolveMetaParentReportingChannels(normalizedPlatformKey);
  const metaFamilyChannels = importedMetaReportingChannels || metaParentReportingChannels;
  const useMetaFamilyRecordedAggregation = Boolean(metaFamilyChannels);
  const snapshotChannelKeys = metaFamilyChannels || [snapshotChannel];
  const snapshotQuery = new URLSearchParams({
    advertiserId: String(normalizedAdvertiserId),
    channel: snapshotChannelKeys[0],
    datePreset: normalizedTimeframe.datePreset,
    allowLatestFallback:
      normalizedTimeframe.start && normalizedTimeframe.end ? "0" : "1",
  });
  if (snapshotCampaignId != null) snapshotQuery.set("campaignId", String(snapshotCampaignId));
  if (normalizedTimeframe.start) snapshotQuery.set("startDate", normalizedTimeframe.start);
  if (normalizedTimeframe.end) snapshotQuery.set("endDate", normalizedTimeframe.end);
  const snapshotPath = buildSnapshotPathForChannel(snapshotChannelKeys[0]);

  const gaQuery = new URLSearchParams({
    advertiserId: String(normalizedAdvertiserId),
    datePreset: normalizedTimeframe.datePreset,
  });
  if (normalizedTimeframe.start) gaQuery.set("startDate", normalizedTimeframe.start);
  if (normalizedTimeframe.end) gaQuery.set("endDate", normalizedTimeframe.end);
  const gaPath = `/api/google-analytics/summary?${gaQuery.toString()}`;

  tracePlatformRequest("snapshot_request", {
    requestUrl: apiUrl(snapshotPath),
    requestPath: snapshotPath,
    query: Object.fromEntries(snapshotQuery.entries()),
    normalizedTimeframe,
    displayTimeframe,
    advertiserFlightRange: advertiserFlightRange || null,
    flightRangeForPacing,
  });

  const settledResults = await Promise.allSettled([
    ...snapshotChannelKeys.map((channelKey) =>
      fetchJson(buildSnapshotPathForChannel(channelKey), { signal })
    ),
    fetchJson(gaPath, { signal }),
  ]);
  let primaryEvidenceRefused = false;
  const snapshotResults = settledResults.slice(0, snapshotChannelKeys.length).map((result, index) => {
    if (result.status !== "fulfilled") {
      if ([401, 403].includes(Number(result.reason?.status))) primaryEvidenceRefused = true;
      return result;
    }
    const requestedScope = {
      advertiserId: normalizedAdvertiserId, campaignId: snapshotCampaignId,
      channel: snapshotChannelKeys[index], timeframe: effectiveSnapshotTimeframe,
    };
    const validScope = snapshotScopeMatches(result.value, requestedScope);
    if (validScope && !hasContradictorySnapshotEnvelope(result.value)) return result;
    if (!isIdentifiedHistoricalFallback(result.value, requestedScope)) primaryEvidenceRefused = true;
    tracePlatformRequest("snapshot_scope_refused", { channel: snapshotChannelKeys[index],
      requestedCampaignId: snapshotCampaignId, requestedTimeframe: normalizedTimeframe,
      responseAdvertiserId: result.value?.advertiserId, responseCampaignId: result.value?.campaignId,
      responseChannel: result.value?.channel, responseWindow: result.value?.window });
    return { status: "fulfilled", value: null };
  });
  const snapshotResult = snapshotResults[0];
  const primaryMetaScope = {
    advertiserId: normalizedAdvertiserId, campaignId: snapshotCampaignId,
    timeframe: effectiveSnapshotTimeframe,
  };
  const primaryMetaCoverage = useMetaFamilyRecordedAggregation
    ? resolveCompleteMetaRecovery(snapshotChannelKeys, snapshotResults, primaryMetaScope) : null;
  // Every family member must supply applicable canonical or exact-window
  // stored evidence; legacy partial rows cannot establish a family total.
  const primaryMetaSnapshot = primaryMetaCoverage
    ? mergeMetaFamilyRecordedSnapshots(snapshotChannelKeys, primaryMetaCoverage.settledResults)
    : null;
  const reportedZeroInRequestedScope = !primaryEvidenceRefused && (useMetaFamilyRecordedAggregation
    ? primaryMetaCoverage?.reportedZero === true : snapshotResults.some((result, index) =>
    result.status === "fulfilled" && hasReportedSnapshotZero(result.value, {
      advertiserId: normalizedAdvertiserId, campaignId: snapshotCampaignId,
      channel: snapshotChannelKeys[index], timeframe: effectiveSnapshotTimeframe,
    })
  ));
  const gaResult = settledResults[settledResults.length - 1];

  let snapshot = primaryEvidenceRefused ? null : primaryMetaCoverage
    ? { ...primaryMetaSnapshot, kpis: primaryMetaCoverage.kpis,
        metaFamilyRecordedAggregation: { ...primaryMetaSnapshot.metaFamilyRecordedAggregation,
          primaryCoverage: { complete: true, publishers: primaryMetaCoverage.publishers } } }
    : useMetaFamilyRecordedAggregation ? null
    : snapshotResult.status === "fulfilled"
    ? snapshotResult.value
    : null;
  let gaSummary =
    !primaryEvidenceRefused && gaResult.status === "fulfilled" ? gaResult.value : null;
  let metricsTimeframe = effectiveSnapshotTimeframe;
  let recoveredFromAutoFlightWindow = false;
  let reportedZeroInRecoveryScope = false;

  tracePlatformRequest("snapshot_response", {
    requestUrl: apiUrl(snapshotPath),
    snapshotStatus: snapshotResult.status,
    snapshotError:
      snapshotResult.status === "rejected"
        ? {
            message: snapshotResult.reason?.message || String(snapshotResult.reason || ""),
            status: snapshotResult.reason?.status || null,
            payload: snapshotResult.reason?.payload || null,
          }
        : null,
    snapshotFound: snapshot?.found ?? null,
    snapshotSource: snapshot?.source || null,
    snapshotSourceTier: snapshot?.sourceTier || null,
    snapshotSourceLabel: getSnapshotSourceLabel(snapshot),
    snapshotDataSource: snapshot?.snapshot?.data_source || null,
    snapshotId: snapshot?.snapshot?.id || null,
    resolverVersion: snapshot?.resolverVersion || null,
    responseWindow: snapshot?.window || null,
    kpis: snapshot?.kpis || null,
    gaStatus: gaResult.status,
  });

  if (
    !primaryEvidenceRefused && shouldAttemptRollingLiveRecovery({
      snapshot,
      normalizedPlatformKey,
      normalizedTimeframe,
      reportedZero: reportedZeroInRequestedScope,
    })
  ) {
    // Pin the existing last_30d preset once, so a recovery response can be
    // checked against the window actually requested, even across midnight.
    // The recovery window ends on the Eastern reporting day (it is sent as an
    // explicit range, so the backend answers for exactly these dates).
    const recoveryEnd = canonicalReportingDay();
    const recoveryStart = new Date(`${recoveryEnd}T12:00:00Z`);
    recoveryStart.setUTCDate(recoveryStart.getUTCDate() - 30);
    const recoveryTimeframe = {
      datePreset: "last_30d",
      start: recoveryStart.toISOString().slice(0, 10),
      end: recoveryEnd,
      key: "last_30d",
      source: "live_window_recovery",
      isExplicitOverride: true,
    };
    const buildRecoverySnapshotQuery = channel => {
      const recoverySnapshotQuery = new URLSearchParams({
        advertiserId: String(normalizedAdvertiserId),
        channel,
        datePreset: recoveryTimeframe.datePreset,
        startDate: recoveryTimeframe.start,
        endDate: recoveryTimeframe.end,
        allowLatestFallback: "1",
      });
      if (snapshotCampaignId != null) recoverySnapshotQuery.set("campaignId", String(snapshotCampaignId));
      return recoverySnapshotQuery;
    };
    const recoverySnapshotQuery = buildRecoverySnapshotQuery(snapshotChannelKeys[0]);
    const recoveryGaQuery = new URLSearchParams({
      advertiserId: String(normalizedAdvertiserId),
      datePreset: recoveryTimeframe.datePreset,
    });
    const recoverySnapshotPath = `/api/optimus/snapshot?${recoverySnapshotQuery.toString()}`;
    const recoveryGaPath = `/api/google-analytics/summary?${recoveryGaQuery.toString()}`;

    tracePlatformRequest("rolling_live_recovery_request", {
      reason: {
        snapshotFound: coerceSnapshotFound(snapshot),
        hasUsableSnapshotMetrics: hasUsableSnapshotMetrics(snapshot),
        latestSnapshotFallback: isLatestSnapshotFallback(snapshot),
      },
      requestUrl: apiUrl(recoverySnapshotPath),
      requestPath: recoverySnapshotPath,
      query: Object.fromEntries(recoverySnapshotQuery.entries()),
      requestedTimeframe: normalizedTimeframe,
      recoveryTimeframe,
    });

    const recoveryResults = await Promise.allSettled([
      ...snapshotChannelKeys.map(channel => fetchJson(
        `/api/optimus/snapshot?${buildRecoverySnapshotQuery(channel).toString()}`, { signal }
      )),
      fetchJson(recoveryGaPath, { signal }),
    ]);
    const recoverySnapshotResults = recoveryResults.slice(0, snapshotChannelKeys.length);
    const recoverySnapshotResult = recoverySnapshotResults[0];
    const recoveryGaResult = recoveryResults[recoveryResults.length - 1];
    const recoveryScope = { advertiserId: normalizedAdvertiserId, campaignId: snapshotCampaignId,
      timeframe: recoveryTimeframe };
    const recoveryCoverage = useMetaFamilyRecordedAggregation
      ? resolveCompleteMetaRecovery(snapshotChannelKeys, recoverySnapshotResults, recoveryScope) : null;
    const mergedRecovery = recoveryCoverage
      ? mergeMetaFamilyRecordedSnapshots(snapshotChannelKeys, recoveryCoverage.settledResults) : null;
    const recoverySnapshot = useMetaFamilyRecordedAggregation
      ? (mergedRecovery ? { ...mergedRecovery, kpis: recoveryCoverage.kpis,
          metaFamilyRecordedAggregation: { ...mergedRecovery.metaFamilyRecordedAggregation,
            recoveryCoverage: { complete: true, publishers: recoveryCoverage.publishers } } } : null)
      : recoverySnapshotResult.status === "fulfilled" && snapshotScopeMatches(recoverySnapshotResult.value,
          { ...recoveryScope, channel: snapshotChannel }) ? recoverySnapshotResult.value : null;
    const recoveryReportedZero = useMetaFamilyRecordedAggregation ? recoveryCoverage?.reportedZero === true
      : hasReportedSnapshotZero(recoverySnapshot, { ...recoveryScope, channel: snapshotChannel });
    const recoveryAccepted = useMetaFamilyRecordedAggregation ? Boolean(recoveryCoverage)
      : shouldUseRecoverySnapshot(recoverySnapshot, recoveryReportedZero);
    tracePlatformRequest("rolling_live_recovery_response", {
      snapshotStatus: recoverySnapshotResult.status,
      snapshotError:
        recoverySnapshotResult.status === "rejected"
          ? {
              message:
                recoverySnapshotResult.reason?.message ||
                String(recoverySnapshotResult.reason || ""),
              status: recoverySnapshotResult.reason?.status || null,
              payload: recoverySnapshotResult.reason?.payload || null,
            }
          : null,
      snapshotFound: recoverySnapshot?.found ?? null,
      snapshotSource: recoverySnapshot?.source || null,
      snapshotSourceTier: recoverySnapshot?.sourceTier || null,
      snapshotSourceLabel: getSnapshotSourceLabel(recoverySnapshot),
      snapshotDataSource: recoverySnapshot?.snapshot?.data_source || null,
      snapshotId: recoverySnapshot?.snapshot?.id || null,
      accepted: recoveryAccepted,
      publisherCoverage: recoveryCoverage?.publishers || null,
      gaStatus: recoveryGaResult.status,
    });

    if (recoveryAccepted) {
      snapshot = recoverySnapshot;
      reportedZeroInRecoveryScope = recoveryReportedZero;
      gaSummary =
        recoveryGaResult.status === "fulfilled" ? recoveryGaResult.value : gaSummary;
      metricsTimeframe = recoveryTimeframe;
      recoveredFromAutoFlightWindow = true;
    }
  }

  const shouldApplyAdvertiserGaSupport =
    !normalizedPlatformKey ||
    normalizedPlatformKey === "all" ||
    normalizedPlatformKey === "google_search" ||
    normalizedPlatformKey === "googlesearch" ||
    normalizedPlatformKey === "google";

  const kpis = getSnapshotMetricBag(snapshot);
  const spendEfficiency = resolveSpendEfficiencyContract(snapshot);
  const spendEfficiencyMetric = resolveSpendEfficiencyMetric(spendEfficiency, {
    platformKey:
      normalizedPlatformKey && normalizedPlatformKey !== "all"
        ? normalizedPlatformKey
        : null,
    metricKey: "cpqv",
  });
  const completedViewCostMetric = resolveSpendEfficiencyMetric(spendEfficiency, {
    platformKey:
      normalizedPlatformKey && normalizedPlatformKey !== "all"
        ? normalizedPlatformKey
        : null,
    metricKey: "cpcv",
  });
  const contractedPlatformSpend =
    normalizedPlatformKey && normalizedPlatformKey !== "all"
      ? resolveSpendEfficiencySpend(spendEfficiency, normalizedPlatformKey)
      : null;
  const spendBasis = spendEfficiency?.spendBasis || requestedSpendBasis;
  const snapshotWindow = extractSnapshotWindow(snapshot);
  const snapshotStatedTimeZone = responseReportingTimeZone(snapshot);
  const resolvedDisplayTimeframe = {
    ...(recoveredFromAutoFlightWindow
      ? metricsTimeframe
      : {
          ...displayTimeframe,
          ...metricsTimeframe,
          ...(snapshotWindow || {}),
        }),
    // The window's days are the calendar the answer itself states — a Pacific
    // answer stays labelled Pacific — never the portal's own default.
    ...(snapshotStatedTimeZone ? { timeZone: snapshotStatedTimeZone } : {}),
  };
  const requestedReportingWindow = reportingWindowRecord(normalizedTimeframe);
  const effectiveReportingWindow = reportingWindowRecord(resolvedDisplayTimeframe);
  const requestedAnalysisWindow = describeAnalysisWindow(normalizedTimeframe);
  // Wave 3: served-billed-or-null. `kpis.spend` is itself served
  // billed-or-null for client tokens; the old provider/net tails
  // (`mediaCost`, `cost`) are gone — no raw fallback, fail closed.
  const windowMetricBundle = readCanonicalMetricBundle(kpis);
  const spendRaw = windowMetricBundle.spend;
  const impressionsRaw = windowMetricBundle.impressions;
  const clicksRaw = windowMetricBundle.clicks;
  const sourceCtrRaw = windowMetricBundle.ctr;
  const cpmRaw = windowMetricBundle.cpm;
  const engagementsRaw = windowMetricBundle.engagements;
  const videoViewsRaw = windowMetricBundle.videoViews;
  const videoStartsRaw = windowMetricBundle.videoStarts;
  const videoCompletionsRaw = windowMetricBundle.videoCompletions;
  const vcrRaw = windowMetricBundle.vcr;
  const conversionsRaw = windowMetricBundle.conversions;
  const gaOverview =
    gaSummary?.summary?.overview ||
    gaSummary?.overview ||
    gaSummary?.google_analytics?.summary?.overview ||
    null;
  const gaSessionsRaw = shouldApplyAdvertiserGaSupport
    ? toNumber(gaOverview?.sessions) ?? pickFirstNumber(kpis?.sessions)
    : pickFirstNumber(kpis?.sessions);
  const gaConversionsRaw = shouldApplyAdvertiserGaSupport
    ? toNumber(
        gaOverview?.conversions ||
          gaOverview?.keyEvents ||
          gaSummary?.summary?.conversions ||
          gaSummary?.conversions
      )
    : null;

  const effectiveConversions =
    conversionsRaw != null && conversionsRaw >= 0
      ? conversionsRaw
      : gaConversionsRaw != null && gaConversionsRaw >= 0
      ? gaConversionsRaw
      : null;

  /*
   * A server-provided CPC WINS over the local totals division: the canonical
   * layer scopes CPC to click-eligible spend, and spendRaw / clicksRaw here
   * would silently re-blend ineligible (e.g. CTV) spend back in. The local
   * derivation survives only for payloads that carry no cpc at all.
   */
  const cpcRaw =
    pickFirstNumber(kpis?.cpc, kpis?.costPerClick) ??
    (spendRaw != null && clicksRaw != null && clicksRaw > 0
      ? spendRaw / clicksRaw
      : null);
  const cpaRaw =
    spendRaw != null && effectiveConversions != null && effectiveConversions > 0
      ? spendRaw / effectiveConversions
      : pickFirstNumber(kpis?.cpa, kpis?.costPerAcquisition);

  const pricingMetrics = shouldApplyProgrammaticCampaignPricing
    ? computeClientProgrammaticCommercialMetrics({
        campaign,
        platformKey: normalizedPlatformKey,
        metrics: kpis,
        impressions: impressionsRaw,
        clicks: clicksRaw,
        conversions: effectiveConversions,
      })
    : null;
  const aggregateCommercialMetrics = shouldUseAggregateClientFacingSpend
    ? resolveAggregateClientFacingMetrics({
        snapshot,
      })
    : null;

  const effectiveSpendRaw =
    normalizedPlatformKey === "youtube" || normalizedPlatformKey === "ctv"
      ? contractedPlatformSpend
      : aggregateCommercialMetrics?.billedSpend ??
        pricingMetrics?.billedCost ??
        spendRaw;
  const effectiveImpressionsRaw =
    aggregateCommercialMetrics?.impressions ?? impressionsRaw;
  const effectiveClicksRaw = aggregateCommercialMetrics?.clicks ?? clicksRaw;
  const effectiveConversionsRaw =
    aggregateCommercialMetrics?.conversions ?? effectiveConversions;
  const effectiveEngagementsRaw =
    aggregateCommercialMetrics?.engagements ?? engagementsRaw;
  const effectiveVideoViewsRaw =
    aggregateCommercialMetrics?.videoViews ?? videoViewsRaw;
  const effectiveVideoStartsRaw =
    aggregateCommercialMetrics?.videoStarts ?? videoStartsRaw;
  const effectiveVideoCompletionsRaw =
    aggregateCommercialMetrics?.videoCompletions ?? videoCompletionsRaw;
  const effectiveVcrRaw = aggregateCommercialMetrics?.vcr ?? vcrRaw;
  const effectiveCpmRaw =
    aggregateCommercialMetrics?.billedCpm ?? pricingMetrics?.billedCpm ?? cpmRaw;
  const effectiveCpcRaw =
    aggregateCommercialMetrics?.billedCpc ?? pricingMetrics?.billedCpc ?? cpcRaw;
  const effectiveCpaRaw =
    aggregateCommercialMetrics?.billedCpa ?? pricingMetrics?.billedCpa ?? cpaRaw;
  // Wave 3: provider/net figures are no longer served to client tokens, so
  // provider spend/CPM are always null here — never a re-labeled billed value
  // and never a raw fallback (matches the canonical-summary path above).
  const providerSpendRaw = null;
  const providerCpmRaw = null;
  const pricingApplied =
    aggregateCommercialMetrics?.pricingApplied ?? Boolean(pricingMetrics);
  const pricingAppliedPlatforms =
    aggregateCommercialMetrics?.pricingAppliedPlatforms || [];
  const pricingFallbackPlatforms =
    aggregateCommercialMetrics?.pricingFallbackPlatforms || [];
  const effectivePricingModel =
    pricingMetrics?.pricingModel ||
    (aggregateCommercialMetrics?.pricingApplied ? "mixed_aggregate" : null);
  const effectivePricingLineItemKey =
    pricingMetrics?.lineItemKey ||
    (aggregateCommercialMetrics?.pricingApplied ? "aggregate" : null);
  const effectiveCtrRaw =
    (!normalizedPlatformKey || normalizedPlatformKey === "all"
      ? deriveCrossChannelCtrPercent(snapshot?.byPlatform)
      : null) ??
    deriveCtrPercentFromClicks({
      clicks: effectiveClicksRaw,
      impressions: effectiveImpressionsRaw,
      channel: normalizedPlatformKey,
    }) ??
    sourceCtrRaw;

  const platformMix = normalizePlatformArray(
    campaign?.platforms ||
      campaign?.target_platforms ||
      campaign?.targetPlatforms ||
      campaign?.strategy_payload?.platforms
  );
  const hasMeaningfulSignal = hasPositiveSignal(
    effectiveSpendRaw,
    effectiveImpressionsRaw,
    effectiveClicksRaw,
    effectiveConversionsRaw,
    effectiveEngagementsRaw,
    effectiveVideoViewsRaw,
    effectiveVideoStartsRaw,
    effectiveVideoCompletionsRaw,
    gaSessionsRaw
  );
  const snapshotResolvedWithMetrics =
    coerceSnapshotFound(snapshot) === true &&
    hasPositiveSignal(
      effectiveSpendRaw,
      effectiveImpressionsRaw,
      effectiveClicksRaw,
      effectiveConversionsRaw,
      effectiveEngagementsRaw,
      effectiveVideoViewsRaw,
      effectiveVideoStartsRaw,
      effectiveVideoCompletionsRaw,
      gaSessionsRaw
    );
  const topPlatform = hasMeaningfulSignal
    ? aggregateCommercialMetrics?.topPlatform ||
      (normalizedPlatformKey
        ? normalizePlatformLabel(normalizedPlatformKey)
        : chooseTopPlatform(snapshot?.byPlatform, platformMix) || null)
    : null;
  const resolvedFlightRangeForPacing = flightRangeForPacing || snapshotWindow;

  const pacing = buildPacingStatus({
    campaign,
    spend: effectiveSpendRaw,
    flightRange: resolvedFlightRangeForPacing,
  });
  const advertiserName = String(
    campaign?.advertiser_name ||
      campaign?.advertiser ||
      gaSummary?.advertiser?.name ||
      ""
  ).trim();

  const costLabel = effectiveCpaRaw != null ? "CPA" : "CPC";
  const costValue = effectiveCpaRaw != null ? effectiveCpaRaw : effectiveCpcRaw;

  const hasAnyData = hasMeaningfulSignal || snapshotResolvedWithMetrics ||
    (!recoveredFromAutoFlightWindow && reportedZeroInRequestedScope) || reportedZeroInRecoveryScope;

  const metricsPayload = {
    spend: effectiveSpendRaw,
    impressions: effectiveImpressionsRaw,
    ctr: effectiveCtrRaw,
    cpc: effectiveCpcRaw,
    cpm: effectiveCpmRaw,
    cpa: effectiveCpaRaw,
    conversions: effectiveConversionsRaw,
    engagements: effectiveEngagementsRaw,
    videoViews: effectiveVideoViewsRaw,
    videoStarts: effectiveVideoStartsRaw,
    videoCompletions: effectiveVideoCompletionsRaw,
    vcr: effectiveVcrRaw,
    cpqv: spendEfficiencyMetric?.value ?? null,
    cpcv: completedViewCostMetric?.value ?? null,
    sessions: gaSessionsRaw,
    topPlatform,
    pacingStatus: pacing.label,
    objective:
      campaign?.objective ||
      campaign?.goal_type ||
      campaign?.goalType ||
      null,
    flightStart:
      parseCampaignDate(campaign?.flight_start_date) ||
      parseCampaignDate(campaign?.flightStart) ||
      resolvedFlightRangeForPacing?.start ||
      null,
    flightEnd:
      parseCampaignDate(campaign?.flight_end_date) ||
      parseCampaignDate(campaign?.flightEnd) ||
      resolvedFlightRangeForPacing?.end ||
      null,
    platformMix,
    platformKey: normalizedPlatformKey || "all",
  };

  const metricSignature = buildMetricSignature(metricsPayload);

  const analysisWindow = describeAnalysisWindow(resolvedDisplayTimeframe);

  const result = {
    spend:
      formatCurrency(
        effectiveSpendRaw,
        effectiveSpendRaw > 0 && effectiveSpendRaw < 1 ? 2 : 0
      ) || null,
    impressions: formatInteger(effectiveImpressionsRaw) || null,
    ctr: formatPercentScale(effectiveCtrRaw) || null,
    cpc_or_cpa: formatCurrency(costValue, 2) || null,
    cpm: formatCurrency(effectiveCpmRaw, 2) || null,
    clicks: formatInteger(effectiveClicksRaw) || null,
    conversions: formatInteger(effectiveConversionsRaw) || null,
    videoViews: formatInteger(effectiveVideoViewsRaw) || null,
    videoStarts: formatInteger(effectiveVideoStartsRaw) || null,
    videoCompletions: formatInteger(effectiveVideoCompletionsRaw) || null,
    vcr: formatPercent(effectiveVcrRaw) || null,
    costLabel,
    topPlatform,
    pacingStatus: pacing.label,
    pacingMeta: pacing.meta,
    pacingTone: pacing.tone,
    insightSummary:
      buildFallbackInsight({
        advertiserName,
        topPlatform,
        pacingStatus: pacing.label,
        spend: effectiveSpendRaw,
        ctr: effectiveCtrRaw,
        gaSessions: gaSessionsRaw,
        impressions: effectiveImpressionsRaw,
        clicks: effectiveClicksRaw,
        conversions: effectiveConversionsRaw,
        engagements: effectiveEngagementsRaw,
        videoViews: effectiveVideoViewsRaw,
      }),
    scopeLabel: normalizedPlatformKey
      ? `${normalizePlatformLabel(normalizedPlatformKey)} Analytics`
      : advertiserName || "Advertiser scope pending",
    windowLabel: analysisWindow.label,
    analysisWindow,
    requestedWindowLabel: requestedAnalysisWindow.label,
    requestedAnalysisWindow,
    raw: {
      requestedReportingWindow,
      effectiveReportingWindow,
      // The calendar the snapshot's canonical evidence states (null when none).
      reportingTimeZone: responseReportingTimeZone(snapshot),
      reportingCalendar:
        snapshot?.canonicalReporting?.reportingCalendar ||
        snapshot?.canonicalSummary?.reportingCalendar ||
        null,
      spend: effectiveSpendRaw,
      impressions: effectiveImpressionsRaw,
      ctr: effectiveCtrRaw,
      cpc: effectiveCpcRaw,
      cpm: effectiveCpmRaw,
      cpa: effectiveCpaRaw,
      clicks: effectiveClicksRaw,
      conversions: effectiveConversionsRaw,
      engagements: effectiveEngagementsRaw,
      videoViews: effectiveVideoViewsRaw,
      videoStarts: effectiveVideoStartsRaw,
      videoCompletions: effectiveVideoCompletionsRaw,
      videoPlayedTo100Count: windowMetricBundle.videoPlayedTo100Count,
      ...Object.fromEntries([25, 50, 75, 100].map(milestone => [
        `youtubePlayed${milestone}Rate`, windowMetricBundle[`youtubePlayed${milestone}Rate`],
      ])),
      vcr: effectiveVcrRaw,
      cpqv: spendEfficiencyMetric?.value ?? null,
      cpcv: completedViewCostMetric?.value ?? null,
      costPerQualifiedVideoView: spendEfficiencyMetric,
      costPerCompletedView: completedViewCostMetric,
      spendEfficiency,
      sessions: gaSessionsRaw,
      gaConversions: gaConversionsRaw,
      topPlatform,
      pacingStatus: pacing.label,
      snapshotSource: snapshot?.source || null,
      snapshotSourceTier: snapshot?.sourceTier || null,
      snapshotSourceLabel: getSnapshotSourceLabel(snapshot),
      snapshotSourceConfidence:
        snapshot?.sourceConfidence != null ? Number(snapshot.sourceConfidence) : null,
      snapshotSourceTimestamp: snapshot?.sourceTimestamp || null,
      snapshotSourceUrl: snapshot?.sourceUrl || null,
      snapshotSourceNotes: Array.isArray(snapshot?.sourceNotes) ? snapshot.sourceNotes : [],
      snapshotSourceLimitations: Array.isArray(snapshot?.sourceLimitations)
        ? snapshot.sourceLimitations
        : [],
      snapshotSourceDimensions:
        snapshot && snapshot.sourceDimensions && typeof snapshot.sourceDimensions === "object"
          ? snapshot.sourceDimensions
          : null,
      snapshotIsLive: snapshot?.isLive === true,
      snapshotIsFallback:
        snapshot?.isFallback === true ||
        snapshot?.sourceTier === "report_url_scan" ||
        snapshot?.sourceTier === "stored_snapshot",
      snapshotFound: coerceSnapshotFound(snapshot),
      snapshotUsedFallback:
        snapshot?.usedFallback === true ||
        snapshot?.sourceTier === "stored_snapshot" ||
        snapshot?.source === "ingested",
      snapshotResolverVersion: snapshot?.resolverVersion || null,
      snapshotDataSource:
        snapshot?.snapshot?.data_source ||
        snapshot?.dataSourcesByPlatform?.[normalizedPlatformKey] ||
        null,
      metricSignature,
      scopeKey: [
        normalizedAdvertiserId,
        normalizedPlatformKey || "all",
        metricsTimeframe?.key || "",
        metricsTimeframe?.start || "",
        metricsTimeframe?.end || "",
        normalizedTimeframe?.key || "",
      ].join(":"),
      campaignId: snapshotCampaignId,
      platformKey: normalizedPlatformKey || "all",
      toplineResolverVersion: CONNECTED_PLATFORM_TOPLINE_VERSION,
      spendBasis,
      /*
       * Client vocabulary only. The old default hardcoded the internal basis
       * label whenever the server omitted one — which is exactly what a
       * client-safe server payload does.
       */
      spendBasisClientLabel:
        resolveClientSpendBasisLabel(spendEfficiency) || "Client-billed",
      pricingApplied,
      pricingModel: effectivePricingModel,
      pricingLineItemKey: effectivePricingLineItemKey,
      pricingAppliedPlatforms,
      pricingFallbackPlatforms,
      mediaSpend: providerSpendRaw,
      mediaCpm: providerCpmRaw,
      providerSpend: providerSpendRaw,
      providerCpm: providerCpmRaw,
      clientFacingSpend: effectiveSpendRaw,
      clientFacingCpm: effectiveCpmRaw,
      billedSpend: effectiveSpendRaw,
      billedCpm: effectiveCpmRaw,
      datePreset: effectiveReportingWindow.datePreset,
      startDate: effectiveReportingWindow.start,
      endDate: effectiveReportingWindow.end,
      timeframeKey: effectiveReportingWindow.key,
      requestedDatePreset: normalizedTimeframe.datePreset,
      requestedStartDate: normalizedTimeframe.start,
      requestedEndDate: normalizedTimeframe.end,
      requestedTimeframeKey: normalizedTimeframe.key,
      requestedTimeframeSource: normalizedTimeframe.source,
      recoveredFromAutoFlightWindow,
      flightStart: resolvedFlightRangeForPacing?.start || resolvedDisplayTimeframe?.start || null,
      flightEnd: resolvedFlightRangeForPacing?.end || resolvedDisplayTimeframe?.end || null,
      flightContextSource:
        normalizedTimeframe.source && String(normalizedTimeframe.source).includes("flight")
          ? normalizedTimeframe.source
          : resolvedFlightRangeForPacing
          ? "resolved_flight_context"
          : null,
      analysisWindowSource:
        resolvedDisplayTimeframe?.source || displayTimeframe.source || metricsTimeframe.source || null,
      analysisWindowMode: analysisWindow.mode,
      analysisWindowSubtext: analysisWindow.subtext,
      metaFamilyRecordedAggregation: snapshot?.metaFamilyRecordedAggregation || null,
      dailyDelivery: resolveSnapshotDailyDelivery(snapshot, {
        merged: snapshotChannelKeys.length > 1 || useMetaFamilyRecordedAggregation,
      }),
      /*
       * Site measurement for the same window, carried beside delivery and
       * never folded into it. The endpoint omits it entirely for a channel
       * that may not draw the overlay, so this is null on exactly the pages
       * where an overlay would be a claim about the wrong thing.
       */
      siteVisits: resolveObservedSiteVisits(snapshot?.siteVisits),
      hasAnyData,
      usableDataFound: hasAnyData,
    },
  };

  tracePlatformRequest("topline_result", {
    result,
    gateInputs: {
      spendRaw: effectiveSpendRaw,
      impressionsRaw: effectiveImpressionsRaw,
      clicksRaw: effectiveClicksRaw,
      hasMeaningfulSignal,
      snapshotFound: snapshot?.found ?? null,
      snapshotSource: snapshot?.source || null,
      snapshotSourceTier: snapshot?.sourceTier || null,
      snapshotId: snapshot?.snapshot?.id || null,
    },
  });

  if (hasAnyData) {
    dataCache.set(cacheKey, { ts: Date.now(), value: result });
  }
  return result;
}

export default getToplinePerformance;
